"use client";
import { useState, useEffect, useMemo, useCallback, Fragment } from "react";

import MaterialsSummary3 from "./MaterialsSummary3";
import axios from "axios";
import {
  useParams,
  useNavigate,
  useLocation,
  useSearchParams,
} from "react-router-dom";

import BudgetImpactPanel from "./BudgetImpactPanel";
import ItemSearchHelper3 from "./ItemSearchHelper3";
import VendorCostComparisonPanel from "./VendorCostComparisonPanel"; // ADD THIS LINE

// REPLACE WITH:
import { jwtDecode } from "jwt-decode";
import Select from "react-select";
import {
  BulkActionsBar,
  BulkDeliverModal,
  BulkEmailModal,
  BulkSiteUpdateModal,
  RowCheckbox,
  SelectAllCheckbox,
} from "./BulkOrderActions";

const API = "https://erp-production-72da01c8e651.herokuapp.com/api";

const fmt = (n) =>
  (n ?? 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
const cls = (...c) => c.filter(Boolean).join(" ");

const getRequestNotesText = (r) =>
  [
    r?.notes,
    ...(r?.vendorLogistics || []).map((vl) => vl.notes),
    ...(r?.items || [])
      .filter((i) => (i.notes || "").trim())
      .map((i) => `${i.itemCode}: ${i.notes.trim()}`),
  ]
    .map((n) => (n || "").trim())
    .filter(Boolean)
    .join(" · ");

// True when the item's uom is SF or LF — ordered by the PC but priced per SF/LF
const isCoverageBased = (uom = "") =>
  ["SF", "LF"].some((u) => uom.trim().toUpperCase().includes(u));

// Package cost = coverage × rate/unit.  EA/Box items: coverage=1, same result.
const calcPackageCost = (ratePerUnit, coverage) =>
  parseFloat(ratePerUnit || 0) * (parseFloat(coverage) || 1);

function tokenToFeet(s) {
  // Normalize smart/curly quotes and other inch-mark variants to ASCII "
  s = (s || "").trim().replace(/[\u201C\u201D\u02DD\u02BA\u2033\u22EE]/g, '"');
  if (!s) return null;

  // feet + inches  e.g. 15' 2-3/4"  or  10'6"  or  10' 6.5"
  let m = s.match(
    /^(\d+(?:\.\d+)?)'[\s]*(\d+(?:\.\d+)?(?:-\d+\/\d+)?(?:\/\d+)?|\d*-\d+\/\d+)"$/,
  );
  if (m) return parseFloat(m[1]) + inchStrToFeet(m[2]);

  // feet only  e.g. 10'
  m = s.match(/^(\d+(?:\.\d+)?)'$/);
  if (m) return parseFloat(m[1]);

  // inches with quote — decimal, whole, mixed-fraction, or bare fraction
  //   e.g. 122"  35-3/4"  119-3/4"  109.5"  109"
  m = s.match(/^(\d+(?:\.\d+)?(?:-\d+\/\d+)?(?:\/\d+)?)"$/);
  if (m) return inchStrToFeet(m[1]);

  // bare decimal, no inch mark  e.g. 10.5
  m = s.match(/^(\d+(?:\.\d+)?)$/);
  if (m) return parseFloat(m[1]);

  // bare inch string with no quote mark  e.g. 122  35-3/4  119-3/4
  m = s.match(/^(\d+(?:-\d+\/\d+)?)$/);
  if (m) return inchStrToFeet(m[1]);

  return null;
}

const getTomorrow = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().split("T")[0];
};

function inchStrToFeet(s) {
  s = (s || "").trim();
  // mixed: 35-3/4  (whole-fraction)
  let m = s.match(/^(\d+)-(\d+)\/(\d+)$/);
  if (m) return (parseInt(m[1]) + parseInt(m[2]) / parseInt(m[3])) / 12;
  // fraction only: 3/4
  m = s.match(/^(\d+)\/(\d+)$/);
  if (m) return parseInt(m[1]) / parseInt(m[2]) / 12;
  // decimal or whole number: 122, 109.5
  m = s.match(/^(\d+(?:\.\d+)?)$/);
  if (m) return parseFloat(m[1]) / 12;
  return 0;
}

function SentBadge({ order }) {
  // Explicit flag (new orders) OR inferred from status (old orders that
  // predate the sentToVendor field but could only have gotten here by
  // being sent to the vendor first).
  const sent =
    order.sentToVendor === true ||
    ["partial", "delivered"].includes(order.status);

  if (!sent)
    return (
      <span className="inline-block px-2 py-0.5 text-[10px] font-semibold rounded-full bg-gray-700/60 text-gray-500">
        Not sent
      </span>
    );

  const inferred = order.sentToVendor !== true; // flag missing but status implies it
  return (
    <span
      className={cls(
        "inline-block px-2 py-0.5 text-[10px] font-semibold rounded-full",
        inferred
          ? "bg-purple-900/40 text-purple-300 border border-purple-700/40"
          : "bg-purple-600/70 text-purple-100",
      )}
      title={inferred ? "Inferred from delivery status" : undefined}
    >
      Sent
    </span>
  );
}

function BillStatusBadge({ status }) {
  if (!status)
    return (
      <span className="inline-block px-2 py-0.5 text-[10px] font-semibold rounded-full bg-gray-700/60 text-gray-500">
        No bill
      </span>
    );
  if (status === "processed")
    return (
      <span className="inline-block px-2 py-0.5 text-[10px] font-semibold rounded-full bg-emerald-600/70 text-emerald-100">
        Processed
      </span>
    );
  return (
    <span className="inline-block px-2 py-0.5 text-[10px] font-semibold rounded-full bg-yellow-600/70 text-yellow-100">
      Bill pending
    </span>
  );
}

function parseSizeCoverage(size, uom) {
  if (!size || !uom) return null;
  const u = uom.trim().toUpperCase();
  const s = size.trim();
  if (u.includes("SF")) {
    const parts = s.split(/\s*[xX×]\s*/);
    if (parts.length === 2) {
      const a = tokenToFeet(parts[0].trim());
      const b = tokenToFeet(parts[1].trim());
      if (a !== null && b !== null) return Math.round(a * b * 10000) / 10000;
    }
    return null;
  }
  if (u.includes("LF")) {
    const ft = tokenToFeet(s);
    return ft !== null ? Math.round(ft * 10000) / 10000 : null;
  }
  return null;
}

const STATUS_BADGE = {
  draft: "bg-gray-600/70 text-gray-200",
  submitted: "bg-yellow-600/70 text-yellow-100",
  generating: "bg-blue-600/70 text-blue-100",
  generated: "bg-green-600/70 text-green-100",
  cancelled: "bg-red-800/70 text-red-200",
  pending: "bg-yellow-600/70 text-yellow-100",
  confirmed: "bg-blue-600/70 text-blue-100",
  partial: "bg-orange-600/70 text-orange-100",
  delivered: "bg-green-600/70 text-green-100",
  void: "bg-red-800/70 text-red-200",
};

function StatusBadge({ status }) {
  return (
    <span
      className={cls(
        "inline-block px-2 py-0.5 text-[10px] font-semibold rounded-full",
        STATUS_BADGE[status] || "bg-gray-700 text-gray-300",
      )}
    >
      {status?.charAt(0).toUpperCase() + status?.slice(1)}
    </span>
  );
}

function DuplicateButton({ requestId, token, onDuplicated }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const handle = async () => {
    setBusy(true);
    setErr("");
    try {
      const res = await axios.post(
        `${API}/materialRequests3/${requestId}/duplicate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      onDuplicated(res.data.request);
    } catch (e) {
      setErr(e.response?.data?.message || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <button
        onClick={handle}
        disabled={busy}
        title="Duplicate this request as a new draft"
        className="px-2 py-1 text-[10px] font-medium bg-gray-700 hover:bg-gray-600 text-gray-400 hover:text-white rounded border border-gray-600 transition-colors disabled:opacity-40"
      >
        {busy ? "…" : "⧉ Dupe"}
      </button>
      {err && <p className="text-[9px] text-red-400 mt-0.5">{err}</p>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Price resolution helper
//
// Priority:
//   1. Most recent MO for this itemCode × vendor on this project  (priceMap)
//   2. Vendor price from the item database (vendorPrices[])
//   3. Budget unitCost (bi.unitCost / defaultCost)
//
// Returns { rate, packageCost, source }
// ─────────────────────────────────────────────────────────────────────────────
function resolvePrice(dbItem, vendorId, coverage, isCov, priceMap) {
  // Always resolve from the item database's vendor price for the given
  // vendor (normally the catalog's defaultVendor) — order history
  // (priceMap) is intentionally NOT consulted here, so pricing on a new
  // material request always starts from the database price.
  if (vendorId && dbItem.vendorPrices?.length) {
    const vp = dbItem.vendorPrices.find(
      (v) => (v.vendor?._id || v.vendor)?.toString() === vendorId.toString(),
    );
    if (vp) {
      const rate = vp.price || vp.unitRate || 0;
      return {
        rate,
        packageCost: isCov ? calcPackageCost(rate, coverage) : rate,
        source: "item database",
      };
    }
  }

  // Fallback: budget / default cost (no vendor price on file for this vendor)
  const rate =
    parseFloat(dbItem.unitCost) || parseFloat(dbItem.defaultCost) || 0;
  return {
    rate,
    packageCost: isCov ? calcPackageCost(rate, coverage) : rate,
    source: "budget default",
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Build a form item row from a catalog/budget item + context
// Used by both addItem (new request) and hydrating saved draft items
// ─────────────────────────────────────────────────────────────────────────────
function buildFormItem(
  dbItem,
  { vendorId, qty, priceMap, overrideUnitPrice } = {},
) {
  const uom = dbItem.uom || "EA";
  const covered = isCoverageBased(uom);
  const size = dbItem.size || "";

  const parsedCoverage = covered ? parseSizeCoverage(size, uom) : null;
  const coverage =
    parsedCoverage !== null
      ? parsedCoverage
      : covered
        ? parseFloat(dbItem.coveragePerUnit) || 1
        : 1;

  const resolvedVendorId =
    vendorId ||
    dbItem.defaultVendor?._id?.toString() ||
    (typeof dbItem.defaultVendor === "string" ? dbItem.defaultVendor : "") ||
    "";

  let rate, packageCost, priceSource;

  if (overrideUnitPrice !== undefined) {
    // Draft re-load: use the saved unitPrice verbatim
    packageCost = parseFloat(overrideUnitPrice) || 0;
    rate = covered && coverage > 0 ? packageCost / coverage : packageCost;
    priceSource = "saved";
  } else {
    const resolved = resolvePrice(
      dbItem,
      resolvedVendorId,
      coverage,
      covered,
      priceMap || {},
    );
    rate = resolved.rate;
    packageCost = resolved.packageCost;
    priceSource = resolved.source;
  }

  // Determine vendorPriceStatus
  let vendorPriceStatus = null;
  if (resolvedVendorId && dbItem.vendorPrices?.length) {
    const found = dbItem.vendorPrices.some(
      (vp) =>
        (vp.vendor?._id || vp.vendor)?.toString() ===
        resolvedVendorId.toString(),
    );
    vendorPriceStatus = found ? "found" : "missing";
  } else if (resolvedVendorId) {
    vendorPriceStatus = "missing";
  }

  return {
    itemDatabaseId: dbItem._id || dbItem.itemDatabaseId,
    itemCode: dbItem.itemCode || "",
    description:
      dbItem.description ||
      dbItem.costCodeDescription ||
      dbItem.itemDescription ||
      "",
    size,
    uom,
    sectionNumber: dbItem.sectionNumber || "",
    isCoverage: covered,
    coveragePerUnit: coverage,
    ratePerUnit: rate,
    unitPrice: packageCost,
    vendor: resolvedVendorId,
    // ADD THESE 4 LINES
    defaultVendorId:
      dbItem.defaultVendor?._id?.toString() ||
      (typeof dbItem.defaultVendor === "string" ? dbItem.defaultVendor : "") ||
      "",
    quantity: qty ?? 1,
    notes: "",
    vendorPrices: dbItem.vendorPrices || [],
    vendorPriceStatus,
    priceSource,
    addingVendorPrice: false,
    newVendorPriceInput: "",
    newVendorPartNumber: "",
    vendorPriceSaving: false,
    vendorPriceError: "",
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// RequestForm — used for BOTH new requests and editing drafts
//
// Props:
//   existingRequest  — when editing a draft, the full request object
//   projectId, purchaseOrders, users, token, priceMap, vendors
//   onSaved(request) — called after create or update succeeds
//   onCancel
// ─────────────────────────────────────────────────────────────────────────────
function RequestForm({
  projectId,
  purchaseOrders,
  users,
  token,
  priceMap, // { [itemCode]: { [vendorId]: { unitPrice, coveragePerUnit, size, uom, vendorId, vendorName, orderedAt, orderNumber } } }
  vendors,
  existingRequest, // undefined = new, object = edit draft
  onSaved,
  onCancel,
}) {
  const isEditing = !!existingRequest;

  const [selectedPO, setSelectedPO] = useState(
    existingRequest?.purchaseOrder?._id?.toString() ||
      existingRequest?.purchaseOrder?.toString() ||
      "",
  );
  const [items, setItems] = useState([]);
  const [showSearch, setShowSearch] = useState(false);

  const [recommendedBudgetItems, setRecommendedBudgetItems] = useState([]);
  const [loadingRecs, setLoadingRecs] = useState(false);

  // REPLACE the existing useState for logistics:
  const [logistics, setLogistics] = useState({
    shippingMethod: existingRequest?.shippingMethod || "Delivery",
    expectedDeliveryDate: existingRequest?.expectedDeliveryDate
      ? existingRequest.expectedDeliveryDate.split("T")[0]
      : getTomorrow(), // ← default tomorrow
    deliveryInstructions: existingRequest?.deliveryInstructions || "",
    deliveryContact:
      existingRequest?.deliveryContact?._id?.toString() ||
      existingRequest?.deliveryContact?.toString() ||
      "",
    laborersRequired: existingRequest?.laborersRequired || false,
    laborersQty: existingRequest?.laborersQty || 0,
    notes: existingRequest?.notes || "",
  });

  // Per-vendor logistics: { [vendorId]: { shippingMethod, expectedDeliveryDate, deliveryContact } }
  const [vendorLogistics, setVendorLogistics] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // ── Load budget recommendations when PO changes ──────────────────────────
  useEffect(() => {
    if (!selectedPO) {
      setRecommendedBudgetItems([]);
      return;
    }
    setLoadingRecs(true);
    axios
      .get(`${API}/purchaseOrders3/${selectedPO}/items?limit=5000`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((r) => {
        // Include budget, change_order, and manual (ordered-extra) rows —
        // manual rows are how previously-ordered items/sizes not on the
        // original budget show up, so don't filter them out.
        const usableItems = (r.data.items || []).filter((i) =>
          ["budget", "change_order", "manual", undefined, null].includes(
            i.source,
          ),
        );

        // Suggested qty = what's still remaining to order, not the full
        // budget/CO requiredQty. Prefer the backend's qtyPending (already
        // ordered subtracted) when available; otherwise derive it.
        const remainingQty = (bi) =>
          bi.qtyPending != null
            ? bi.qtyPending
            : Math.max((bi.requiredQty || 0) - (bi.qtyOrdered || 0), 0) ||
              bi.requiredQty ||
              0;

        const seen = new Map();
        usableItems.forEach((bi) => {
          const key =
            (bi.itemCode || "").trim().toLowerCase() +
            "||" +
            (bi.size || "").trim().toLowerCase();

          // NEW: don't let a missing uom fall back to `container` when the
          // item code implies a coverage-based uom the caller just forgot
          // to populate. `container` is only trusted when `uom` is truly
          // absent — never used to overwrite a real LF/SF value.
          const resolvedUom = bi.uom || bi.container || "EA";
          if (!bi.uom && bi.container) {
            console.warn(
              `[recommendedBudgetItems] "${bi.itemCode}" has no uom from PO-items endpoint — falling back to container "${bi.container}". If this item is actually LF/SF, fix the backend projection.`,
            );
          }

          if (seen.has(key)) {
            seen.get(key).suggestedQty =
              (seen.get(key).suggestedQty || 0) + remainingQty(bi);
          } else {
            seen.set(key, {
              itemCode: bi.itemCode,
              itemDatabaseId: bi.itemDatabaseId,
              description:
                bi.costCodeDescription || bi.itemDescription || bi.description,
              size: bi.size,
              uom: resolvedUom,
              sectionNumber: bi.sectionNumber,
              coveragePerUnit: bi.coveragePerUnit || 1,
              unitCost: bi.unitCost || 0,
              defaultCost: bi.unitCost || 0,
              defaultVendor: bi.defaultVendor || null,
              vendorPrices: bi.vendorPrices || [],
              // Suggest only what's still remaining to order.
              suggestedQty: remainingQty(bi) || 1,
              // New: surface origin + live order actuals so the search
              // helper can badge "already ordered" items and show sizes
              // that came from prior orders rather than the original budget.
              origin: bi.source === "manual" ? "ordered" : "budget",
              qtyOrdered: bi.qtyOrdered || 0,
              qtyDelivered: bi.qtyDelivered || 0,
              qtyPending: bi.qtyPending || 0,
            });
          }
        });
        setRecommendedBudgetItems([...seen.values()]);
      })
      .catch(() => setRecommendedBudgetItems([]))
      .finally(() => setLoadingRecs(false));
  }, [selectedPO, token]);

  // ── Unique vendor IDs from current item list ─────────────────────────────
  const uniqueVendorIds = useMemo(
    () => [...new Set(items.map((i) => i.vendor).filter(Boolean))],
    [items],
  );
  const isMultiVendor = uniqueVendorIds.length > 1;

  // Initialise a logistics entry whenever a new vendor appears in items
  useEffect(() => {
    if (uniqueVendorIds.length === 0) return;
    setVendorLogistics((prev) => {
      const next = { ...prev };
      uniqueVendorIds.forEach((vid) => {
        if (!next[vid]) {
          next[vid] = {
            shippingMethod: "Delivery",
            expectedDeliveryDate: getTomorrow(),
            deliveryContact: "",
            deliveryInstructions: "",
            notes: "",
          };
        }
      });
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uniqueVendorIds.join(",")]);

  const updateVendorLogistic = (vendorId, field, value) =>
    setVendorLogistics((prev) => ({
      ...prev,
      [vendorId]: { ...prev[vendorId], [field]: value },
    }));

  // ── When editing a draft: hydrate items from the saved request ───────────
  // We load the saved unitPrice verbatim so the user sees exactly what was
  // last saved.  On vendor change the price map is consulted to update price.
  // ── When editing a draft: hydrate items from the saved request ───────────
  // We load the saved unitPrice verbatim so the user sees exactly what was
  // last saved.  On vendor change the price map is consulted to update price.
  useEffect(() => {
    if (!isEditing) return;

    let cancelled = false;

    const hydrate = async () => {
      const savedItems = existingRequest.items || [];

      const vendorPricesByIdx = {};
      await Promise.all(
        savedItems.map(async (saved, idx) => {
          const itemCode = (saved.itemCode || "").trim();

          if (saved.itemDatabaseId) {
            try {
              const res = await axios.get(
                `${API}/item-database/${saved.itemDatabaseId}`,
                { headers: { Authorization: `Bearer ${token}` } },
              );
              const dbItem = res.data.item || res.data;
              vendorPricesByIdx[idx] = {
                vendorPrices: dbItem.vendorPrices || [],
                resolvedItemDatabaseId: saved.itemDatabaseId,
              };
              return;
            } catch {
              // fall through to code lookup below
            }
          }

          if (itemCode) {
            try {
              const res = await axios.get(
                `${API}/item-database/code/${encodeURIComponent(itemCode)}`,
                { headers: { Authorization: `Bearer ${token}` } },
              );
              const dbItem = res.data.item;
              if (dbItem) {
                vendorPricesByIdx[idx] = {
                  vendorPrices: dbItem.vendorPrices || [],
                  resolvedItemDatabaseId: dbItem._id,
                };
                return;
              }
            } catch {
              // no catalog match — genuinely unlinked custom item
            }
          }

          vendorPricesByIdx[idx] = {
            vendorPrices: [],
            resolvedItemDatabaseId: saved.itemDatabaseId || null,
          };
        }),
      );

      if (cancelled) return;

      const hydratedItems = savedItems.map((saved, idx) => {
        const uom = saved.uom || "EA";
        const covered = isCoverageBased(uom);
        const coverage = parseFloat(saved.coveragePerUnit) || 1;
        const packageCost = parseFloat(saved.unitPrice) || 0;
        const rate =
          covered && coverage > 0 ? packageCost / coverage : packageCost;

        let vendorId =
          saved.vendor?._id?.toString() ||
          (typeof saved.vendor === "string" ? saved.vendor : "") ||
          "";

        const resolved = vendorPricesByIdx[idx] || {
          vendorPrices: [],
          resolvedItemDatabaseId: saved.itemDatabaseId,
        };
        const freshVendorPrices = resolved.vendorPrices;

        // NEW: if this item was saved with no vendor, but one of the
        // fetched vendorPrices exactly matches the saved package cost,
        // auto-select that vendor instead of leaving it Unassigned.
        // Uses a small tolerance for floating-point rounding.
        let autoMatched = false;
        if (!vendorId && packageCost > 0 && freshVendorPrices.length) {
          const TOLERANCE = 0.01;
          const match = freshVendorPrices.find((vp) => {
            const vpRate = vp.price || vp.unitRate || 0;
            const vpPackageCost = covered
              ? calcPackageCost(vpRate, coverage)
              : vpRate;
            return Math.abs(vpPackageCost - packageCost) < TOLERANCE;
          });
          if (match) {
            vendorId = String(match.vendor?._id || match.vendor);
            autoMatched = true;
          }
        }

        let vendorPriceStatus = null;
        if (vendorId) {
          const found = freshVendorPrices.some(
            (vp) => String(vp.vendor?._id || vp.vendor) === vendorId.toString(),
          );
          vendorPriceStatus = found ? "found" : "missing";
        }

        return {
          itemDatabaseId: resolved.resolvedItemDatabaseId,
          itemCode: saved.itemCode || "",
          description: saved.description || "",
          size: saved.size || "",
          uom,
          sectionNumber: saved.sectionNumber || "",
          isCoverage: covered,
          coveragePerUnit: coverage,
          ratePerUnit: rate,
          unitPrice: packageCost,
          vendor: vendorId,
          defaultVendorId:
            (resolved.resolvedItemDatabaseId &&
              recommendedBudgetItems
                .find(
                  (b) =>
                    (b.itemDatabaseId || "").toString() ===
                    (resolved.resolvedItemDatabaseId || "").toString(),
                )
                ?.defaultVendor?._id?.toString()) ||
            "",
          quantity: parseFloat(saved.quantity) || 1,
          notes: saved.notes || "",
          vendorPrices: freshVendorPrices,
          vendorPriceStatus,
          priceSource: autoMatched ? "item database" : "saved",
          addingVendorPrice: false,
          newVendorPriceInput: "",
          newVendorPartNumber: "",
          vendorPriceSaving: false,
          vendorPriceError: "",
          showAllVendors: false,
        };
      });

      setItems(hydratedItems);
    };

    hydrate();

    const savedVL = {};
    (existingRequest.vendorLogistics || []).forEach((vl) => {
      const vid =
        vl.vendor?._id?.toString() ||
        (typeof vl.vendor === "string" ? vl.vendor : "");
      if (vid)
        savedVL[vid] = {
          shippingMethod: vl.shippingMethod || "Delivery",
          expectedDeliveryDate: vl.expectedDeliveryDate
            ? new Date(vl.expectedDeliveryDate).toISOString().split("T")[0]
            : getTomorrow(),
          deliveryContact:
            vl.deliveryContact?._id?.toString() ||
            (typeof vl.deliveryContact === "string"
              ? vl.deliveryContact
              : "") ||
            "",
          deliveryInstructions: vl.deliveryInstructions || "",
          notes: vl.notes || "",
        };
    });
    setVendorLogistics(savedVL);

    return () => {
      cancelled = true;
    };
  }, [isEditing, existingRequest, token]);

  // ── addItem — for new items added via ItemSearchHelper3 ──────────────────

  const addItem = async (dbItem, qtyOverride) => {
    const itemCode = (dbItem.itemCode || "").trim();
    const mapForCode = priceMap?.[itemCode] || {};

    // Always fetch the authoritative item-database record — the
    // recommendation/search-result object passed in may be a lighter
    // projection (e.g. from the PO-items endpoint) that omits or
    // mis-populates vendorPrices, uom, size, and coveragePerUnit.
    // `authoritative` is what we trust for coverage-math fields;
    // `dbItem` is still used for itemCode/description/sectionNumber/
    // defaultVendor, which are safe even on the light projection.
    let freshVendorPrices = dbItem.vendorPrices || [];
    let authoritative = dbItem;
    const itemDatabaseId = dbItem._id || dbItem.itemDatabaseId;
    if (itemDatabaseId) {
      try {
        const res = await axios.get(`${API}/item-database/${itemDatabaseId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const fullItem = res.data.item || res.data;
        freshVendorPrices = fullItem.vendorPrices || [];
        authoritative = fullItem; // trust the real DB record for uom/size/coverage
      } catch {
        // fall back to whatever was passed in
      }
    }

    const uom = authoritative.uom || "EA";
    const covered = isCoverageBased(uom);

    // Pick the vendor with the most recent order history on this project
    // Always start from the catalog's default vendor — order history is
    // no longer used to override the vendor pick when adding a new item.
    let bestVendorId =
      dbItem.defaultVendor?._id?.toString() ||
      (typeof dbItem.defaultVendor === "string" ? dbItem.defaultVendor : "") ||
      "";

    const size = authoritative.size || "";
    const parsedCov = covered ? parseSizeCoverage(size, uom) : null;
    const coverage =
      parsedCov !== null
        ? parsedCov
        : covered
          ? parseFloat(authoritative.coveragePerUnit) || 1
          : 1;

    const { rate, packageCost, source } = resolvePrice(
      { ...dbItem, vendorPrices: freshVendorPrices },
      bestVendorId,
      coverage,
      covered,
      priceMap || {},
    );

    let vendorPriceStatus = null;
    if (bestVendorId && freshVendorPrices.length) {
      vendorPriceStatus = freshVendorPrices.some(
        (vp) => (vp.vendor?._id || vp.vendor)?.toString() === bestVendorId,
      )
        ? "found"
        : "missing";
    } else if (bestVendorId) {
      if (mapForCode[bestVendorId]) vendorPriceStatus = "found";
      else vendorPriceStatus = "missing";
    }

    setItems((p) => [
      ...p,
      {
        itemDatabaseId,
        itemCode,
        description:
          dbItem.description ||
          dbItem.costCodeDescription ||
          dbItem.itemDescription ||
          "",
        size,
        uom,
        sectionNumber: dbItem.sectionNumber || "",
        isCoverage: covered,
        coveragePerUnit: coverage,
        ratePerUnit: rate,
        unitPrice: packageCost,
        vendor: bestVendorId,
        defaultVendorId:
          dbItem.defaultVendor?._id?.toString() ||
          (typeof dbItem.defaultVendor === "string"
            ? dbItem.defaultVendor
            : "") ||
          "",
        quantity: qtyOverride ?? 1,
        notes: "",
        vendorPrices: freshVendorPrices, // ← now always the real, current DB prices
        vendorPriceStatus,
        priceSource: source,
        addingVendorPrice: false,
        newVendorPriceInput: "",
        newVendorPartNumber: "",
        vendorPriceSaving: false,
        vendorPriceError: "",
        showAllVendors: false,
      },
    ]);
  };

  // Bulk-apply a vendor plan from VendorCostComparisonPanel: { [idx]: vendorId }
  // Add near your other useMemo/useCallback hooks in RequestForm

  const applyVendorPlan = useCallback((plan) => {
    Object.entries(plan).forEach(([idx, vendorId]) => {
      updateItem(Number(idx), "vendor", vendorId);
    });
  }, []); // updateItem is a stable setItems wrapper; safe to omit

  const budgetOrderRows = useMemo(
    () =>
      items.map((i) => ({
        itemCode: i.itemCode,
        size: i.size,
        uom: i.uom,
        quantity: parseFloat(i.quantity) || 0,
        unitPrice: parseFloat(i.unitPrice) || 0,
        coveragePerUnit: parseFloat(i.coveragePerUnit) || 1,
      })),
    [items],
  );

  // ── updateItem ────────────────────────────────────────────────────────────
  // ── updateItem ────────────────────────────────────────────────────────────
  const updateItem = (idx, field, value) => {
    setItems((p) =>
      p.map((item, i) => {
        if (i !== idx) return item;
        const next = { ...item, [field]: value };

        if (field === "size" && next.isCoverage) {
          const parsed = parseSizeCoverage(value, next.uom);
          if (parsed !== null) {
            next.coveragePerUnit = parsed;
            next.unitPrice = calcPackageCost(next.ratePerUnit, parsed);
          }
        }

        if (field === "vendor") {
          next.addingVendorPrice = false;
          next.newVendorPriceInput = "";
          next.newVendorPartNumber = "";
          next.vendorPriceError = "";
          next.vendorPriceLoading = false;

          if (value) {
            if (next.vendorPrices?.length) {
              // Fall back to already-loaded item database vendor prices
              const vp = next.vendorPrices.find(
                (v) =>
                  (v.vendor?._id || v.vendor)?.toString() === value.toString(),
              );
              if (vp) {
                const knownRate = vp.price || vp.unitRate || 0;
                next.ratePerUnit = knownRate;
                next.unitPrice = next.isCoverage
                  ? calcPackageCost(knownRate, next.coveragePerUnit)
                  : knownRate;
                next.vendorPriceStatus = "found";
                next.priceSource = "item database";
              } else {
                // vendorPrices array loaded but doesn't include this vendor —
                // still worth a fresh DB check in case it's stale (see effect below)
                next.vendorPriceStatus = "missing";
                next.priceSource = null;
                next.vendorPriceLoading = true;
              }
            } else {
              // No local vendorPrices cache at all — must go to the DB
              next.vendorPriceStatus = "missing";
              next.priceSource = null;
              next.vendorPriceLoading = true;
            }
          } else {
            next.vendorPriceStatus = null;
            next.priceSource = null;
          }
        }

        if (field === "coveragePerUnit" || field === "ratePerUnit") {
          next.unitPrice = next.isCoverage
            ? calcPackageCost(next.ratePerUnit, next.coveragePerUnit)
            : parseFloat(next.ratePerUnit || 0);
        }

        return next;
      }),
    );
  };

  // ── refreshVendorPrice — hits the DB whenever a vendor switch didn't
  //    resolve a price from priceMap or the locally cached vendorPrices ─────
  const refreshVendorPrice = useCallback(
    async (idx, itemDatabaseId, vendorId) => {
      if (!itemDatabaseId || !vendorId) return;
      try {
        const res = await axios.get(`${API}/item-database/${itemDatabaseId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const dbItem = res.data.item || res.data;
        const freshVendorPrices = dbItem.vendorPrices || [];

        setItems((p) =>
          p.map((it, i) => {
            if (i !== idx) return it;
            // Vendor may have changed again while the request was in flight
            if ((it.vendor || "") !== vendorId) return it;

            const vp = freshVendorPrices.find(
              (v) =>
                (v.vendor?._id || v.vendor)?.toString() === vendorId.toString(),
            );

            if (vp) {
              const knownRate = vp.price || vp.unitRate || 0;
              return {
                ...it,
                vendorPrices: freshVendorPrices,
                ratePerUnit: knownRate,
                unitPrice: it.isCoverage
                  ? calcPackageCost(knownRate, it.coveragePerUnit)
                  : knownRate,
                vendorPriceStatus: "found",
                priceSource: "item database",
                vendorPriceLoading: false,
              };
            }
            return {
              ...it,
              vendorPrices: freshVendorPrices,
              vendorPriceStatus: "missing",
              priceSource: null,
              vendorPriceLoading: false,
            };
          }),
        );
      } catch {
        setItems((p) =>
          p.map((it, i) =>
            i !== idx ? it : { ...it, vendorPriceLoading: false },
          ),
        );
      }
    },
    [token],
  );
  // ── loadLatestPrice — find whichever vendor most recently had this
  //    itemCode ordered (highest MO#) and switch the row to that vendor +
  //    that price. This intentionally CAN change the selected vendor.
  // ─────────────────────────────────────────────────────────────────────────
  const findLatestOrderForItem = (itemCode) => {
    const byVendor = priceMap?.[(itemCode || "").trim()];
    if (!byVendor) return null;

    let best = null;
    Object.entries(byVendor).forEach(([vendorId, hist]) => {
      const orderNum = parseInt(hist.orderNumber, 10) || 0;
      if (!best || orderNum > best.orderNum) {
        best = { vendorId, orderNum, hist };
      }
    });
    return best;
  };

  // Like findLatestOrderForItem, but scoped to ONE specific vendor — used by
  // the "Latest from this vendor" button so switching vendors never happens,
  // only the price/rate is refreshed from that vendor's most recent order.
  const findLatestOrderForVendor = (itemCode, vendorId) => {
    if (!vendorId) return null;
    const byVendor = priceMap?.[(itemCode || "").trim()];
    if (!byVendor) return null;
    const hist = byVendor[vendorId];
    if (!hist) return null;
    return { vendorId, orderNum: parseInt(hist.orderNumber, 10) || 0, hist };
  };

  const loadLatestPrice = (idx) => {
    const item = items[idx];
    if (!item) return;

    const latest = findLatestOrderForItem(item.itemCode);
    if (!latest) return; // no order history for this itemCode at all

    const { vendorId, hist } = latest;
    const histCoverage = hist.coveragePerUnit || item.coveragePerUnit || 1;
    const packageCost = parseFloat(hist.unitPrice) || 0;
    const rate = item.isCoverage ? packageCost / histCoverage : packageCost;

    setItems((p) =>
      p.map((it, i) => {
        if (i !== idx) return it;
        const newUnitPrice = it.isCoverage
          ? calcPackageCost(rate, it.coveragePerUnit)
          : rate;
        return {
          ...it,
          vendor: vendorId,
          ratePerUnit: rate,
          unitPrice: newUnitPrice,
          priceSource: hist.orderNumber ? `MO #${hist.orderNumber}` : "history",
          vendorPriceStatus: "found",
          addingVendorPrice: false,
          newVendorPriceInput: "",
          newVendorPartNumber: "",
          vendorPriceError: "",
        };
      }),
    );
  };

  // Refresh price/rate from THIS item's most recent order with the
  // CURRENTLY SELECTED vendor only. Unlike loadLatestPrice, this never
  // changes item.vendor — it only overrides ratePerUnit/unitPrice, and it
  // pulls from order history (priceMap), not the item database.
  const loadLatestPriceForVendor = (idx) => {
    const item = items[idx];
    if (!item || !item.vendor) return;

    const found = findLatestOrderForVendor(item.itemCode, item.vendor);
    if (!found) return; // no order history with this vendor for this item

    const { hist } = found;
    const histCoverage = hist.coveragePerUnit || item.coveragePerUnit || 1;
    const packageCost = parseFloat(hist.unitPrice) || 0;
    const rate = item.isCoverage ? packageCost / histCoverage : packageCost;

    setItems((p) =>
      p.map((it, i) => {
        if (i !== idx) return it;
        const newUnitPrice = it.isCoverage
          ? calcPackageCost(rate, it.coveragePerUnit)
          : rate;
        return {
          ...it,
          // vendor intentionally unchanged
          ratePerUnit: rate,
          unitPrice: newUnitPrice,
          priceSource: hist.orderNumber
            ? `MO #${hist.orderNumber} (vendor)`
            : "history (vendor)",
          vendorPriceStatus: "found",
          addingVendorPrice: false,
          newVendorPriceInput: "",
          newVendorPartNumber: "",
          vendorPriceError: "",
        };
      }),
    );
  };

  // Whether THIS item has order history with the CURRENTLY SELECTED vendor
  const hasLatestPriceForVendor = (item) =>
    !!findLatestOrderForVendor(item.itemCode, item.vendor);

  // Whether ANY order history exists for this itemCode (across all vendors)
  const hasAnyLatestPrice = (item) => !!findLatestOrderForItem(item.itemCode);

  // Trigger the DB refresh whenever an item is flagged vendorPriceLoading
  useEffect(() => {
    items.forEach((it, idx) => {
      if (it.vendorPriceLoading && it.itemDatabaseId && it.vendor) {
        refreshVendorPrice(idx, it.itemDatabaseId, it.vendor);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.map((it) => `${it.vendor}:${it.vendorPriceLoading}`).join(",")]);

  const removeItem = (idx) => setItems((p) => p.filter((_, i) => i !== idx));

  const duplicateItem = (idx) =>
    setItems((p) => {
      const copy = { ...p[idx] };
      const next = [...p];
      next.splice(idx + 1, 0, copy);
      return next;
    });

  // Keys that appear on more than one row — used to warn before save
  const duplicateKeys = useMemo(() => {
    const counts = {};
    items.forEach((item) => {
      const key =
        (item.itemCode || "").trim().toLowerCase() +
        "||" +
        (item.size || "").trim().toLowerCase();
      counts[key] = (counts[key] || 0) + 1;
    });
    return new Set(
      Object.entries(counts)
        .filter(([, v]) => v > 1)
        .map(([k]) => k),
    );
  }, [items]);

  // ── saveVendorPrice ───────────────────────────────────────────────────────
  const saveVendorPrice = async (idx) => {
    const item = items[idx];
    if (!item.itemDatabaseId || !item.vendor || !item.newVendorPriceInput)
      return;

    const rate = parseFloat(item.newVendorPriceInput);
    if (isNaN(rate) || rate <= 0) {
      updateItem(
        idx,
        "vendorPriceError",
        "Enter a valid price greater than 0.",
      );
      return;
    }

    updateItem(idx, "vendorPriceSaving", true);
    updateItem(idx, "vendorPriceError", "");

    try {
      await axios.patch(
        `${API}/item-database/${item.itemDatabaseId}/vendor-price`,
        {
          vendor: item.vendor,
          price: rate,
          partNumber: item.newVendorPartNumber || "",
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setItems((p) =>
        p.map((it, i) => {
          if (i !== idx) return it;
          const existingVPs = it.vendorPrices || [];
          const alreadyThere = existingVPs.some(
            (vp) =>
              (vp.vendor?._id || vp.vendor)?.toString() ===
              it.vendor.toString(),
          );
          const updatedVPs = alreadyThere
            ? existingVPs.map((vp) =>
                (vp.vendor?._id || vp.vendor)?.toString() ===
                it.vendor.toString()
                  ? {
                      ...vp,
                      price: rate,
                      partNumber: it.newVendorPartNumber || vp.partNumber,
                    }
                  : vp,
              )
            : [
                ...existingVPs,
                {
                  vendor: it.vendor,
                  price: rate,
                  unitRate: rate,
                  partNumber: it.newVendorPartNumber || "",
                },
              ];

          const newPkg = it.isCoverage
            ? calcPackageCost(rate, it.coveragePerUnit)
            : rate;

          return {
            ...it,
            ratePerUnit: rate,
            unitPrice: newPkg,
            vendorPrices: updatedVPs,
            vendorPriceStatus: "found",
            priceSource: "item database",
            addingVendorPrice: false,
            newVendorPriceInput: "",
            newVendorPartNumber: "",
            vendorPriceSaving: false,
            vendorPriceError: "",
          };
        }),
      );
    } catch (err) {
      setItems((p) =>
        p.map((it, i) =>
          i !== idx
            ? it
            : {
                ...it,
                vendorPriceSaving: false,
                vendorPriceError: err.response?.data?.message || err.message,
              },
        ),
      );
    }
  };

  // ── handleSubmit — create or update ──────────────────────────────────────
  const handleSubmit = async () => {
    if (!selectedPO) return setError("Please select a Purchase Order.");
    if (!items.length) return setError("Add at least one item.");

    // ── Guard: reject if any two rows share itemCode + size ──────────────────
    if (duplicateKeys.size > 0) {
      return setError(
        "Two or more rows have the same item code and size. " +
          "Adjust the size on the duplicated rows before saving.",
      );
    }

    setSaving(true);

    const payload = {
      purchaseOrderId: selectedPO,
      items: items.map((item) => ({
        itemDatabaseId: item.itemDatabaseId,
        itemCode: item.itemCode,
        sectionNumber: item.sectionNumber || "",
        description: item.description,
        size: item.size,
        uom: item.uom,
        coveragePerUnit: parseFloat(item.coveragePerUnit) || 1,
        quantity: parseFloat(item.quantity) || 0,
        vendor: item.vendor || null,
        unitPrice: parseFloat(item.unitPrice) || 0,
        notes: item.notes,
      })),
      ...logistics,
      expectedDeliveryDate: logistics.expectedDeliveryDate || undefined,
      deliveryContact: logistics.deliveryContact || null,

      vendorLogistics: isMultiVendor
        ? uniqueVendorIds.map((vid) => ({
            vendor: vid,
            shippingMethod: vendorLogistics[vid]?.shippingMethod || "Delivery",
            expectedDeliveryDate:
              vendorLogistics[vid]?.expectedDeliveryDate || getTomorrow(),
            deliveryContact: vendorLogistics[vid]?.deliveryContact || null,
            deliveryInstructions:
              vendorLogistics[vid]?.deliveryInstructions || "",
            notes: vendorLogistics[vid]?.notes || "",
          }))
        : [],
    };

    try {
      let res;
      if (isEditing) {
        res = await axios.put(
          `${API}/materialRequests3/${existingRequest._id}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        onSaved(res.data.request);
      } else {
        res = await axios.post(
          `${API}/materialRequests3/project/${projectId}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        onSaved(res.data.request);
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setSaving(false);
    }
  };

  // ── JSX ───────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-5">
      {error && (
        <div className="px-4 py-2 bg-red-900/50 border border-red-700 rounded-lg text-sm text-red-300">
          {error}
        </div>
      )}

      {/* PO selector — locked when editing */}
      <div>
        <label className="block text-xs text-gray-400 mb-1.5">
          Purchase Order *
        </label>
        <select
          value={selectedPO}
          onChange={(e) => {
            setSelectedPO(e.target.value);
          }}
          className="w-full px-3 py-2 text-sm bg-gray-700 border border-gray-600 rounded-lg text-gray-200 focus:border-orange-500 focus:outline-none"
        >
          <option value="">Select a PO…</option>
          {purchaseOrders.map((po) => (
            <option key={po._id} value={po._id}>
              PO #{po.poNumber} — {po.title}
            </option>
          ))}
        </select>

        {selectedPO && (
          <div className="mt-1.5 flex items-center gap-2">
            {loadingRecs ? (
              <span className="text-[10px] text-gray-500 flex items-center gap-1.5">
                <span className="inline-block w-3 h-3 border border-gray-600 border-t-teal-500 rounded-full animate-spin" />
                Loading budget items…
              </span>
            ) : recommendedBudgetItems.length > 0 ? (
              <span className="text-[10px] text-teal-400 flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-teal-500 inline-block" />
                {recommendedBudgetItems.length} budget items ready — click "Add
                Items" to see them
              </span>
            ) : (
              <span className="text-[10px] text-gray-600">
                No budget items found for this PO
              </span>
            )}
          </div>
        )}
      </div>

      {items.length > 0 && (
        <VendorCostComparisonPanel
          items={items}
          vendors={vendors}
          priceMap={priceMap}
          onApplyVendorPlan={applyVendorPlan}
        />
      )}

      {/* Items section */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-xs text-gray-400">
            Items *
            {items.length > 0 && (
              <span className="ml-1.5 text-gray-600">({items.length})</span>
            )}
          </label>
          <button
            onClick={() => setShowSearch(true)}
            disabled={!selectedPO}
            className={cls(
              "px-3 py-1 text-xs font-medium rounded-lg transition-colors",
              !selectedPO
                ? "bg-gray-700 text-gray-600 cursor-not-allowed"
                : recommendedBudgetItems.length > 0
                  ? "bg-teal-600 hover:bg-teal-500 text-white"
                  : "bg-orange-500 hover:bg-orange-600 text-white",
            )}
          >
            {recommendedBudgetItems.length > 0
              ? `★ Add Items (${recommendedBudgetItems.length} recommended)`
              : "+ Add Item"}
          </button>
        </div>

        {!items.length && (
          <p className="text-xs text-gray-500 py-4 text-center border border-dashed border-gray-600 rounded-lg">
            {selectedPO
              ? recommendedBudgetItems.length > 0
                ? `Click "★ Add Items" to see ${recommendedBudgetItems.length} budget-recommended items, or search for others.`
                : 'No budget items found. Click "+ Add Item" to search the catalog.'
              : "Select a PO above, then add items."}
          </p>
        )}

        <div className="space-y-3">
          {items.map((item, idx) => (
            <div
              key={idx}
              className="bg-gray-700 rounded-xl border border-gray-600 p-3 space-y-2.5"
            >
              {/* Header */}
              {(() => {
                const isDupe = duplicateKeys.has(
                  (item.itemCode || "").trim().toLowerCase() +
                    "||" +
                    (item.size || "").trim().toLowerCase(),
                );
                return (
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] font-mono text-orange-400">
                        {item.itemCode}
                      </p>
                      <p className="text-sm text-gray-200 font-medium leading-snug">
                        {item.description}
                      </p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <p className="text-xs text-gray-500">
                          {item.isCoverage
                            ? `Coverage-based · ordered by PC · priced per ${item.uom}`
                            : "Per unit (EA/Box)"}
                        </p>
                        {/* Price source badge */}
                        {item.priceSource && item.priceSource !== "saved" && (
                          <span
                            className={cls(
                              "text-[9px] px-1.5 py-0.5 rounded font-medium",
                              item.priceSource.startsWith("MO #")
                                ? "bg-blue-900/50 text-blue-300 border border-blue-700/40"
                                : item.priceSource === "item database"
                                  ? "bg-green-900/50 text-green-300 border border-green-700/40"
                                  : "bg-gray-600/50 text-gray-400 border border-gray-600",
                            )}
                          >
                            {item.priceSource.startsWith("MO #")
                              ? `↩ ${item.priceSource}`
                              : item.priceSource}
                          </span>
                        )}
                        {isDupe && (
                          <span className="text-[9px] px-1.5 py-0.5 rounded font-semibold bg-red-900/50 text-red-300 border border-red-700/40">
                            ⚠ duplicate size — adjust before saving
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <button
                        type="button"
                        onClick={() => duplicateItem(idx)}
                        title="Duplicate this row"
                        className="px-2 py-1 text-[10px] font-medium bg-gray-600 hover:bg-gray-500 text-gray-300 hover:text-white rounded border border-gray-500 transition-colors"
                      >
                        ⧉
                      </button>
                      <button
                        onClick={() => removeItem(idx)}
                        className="text-gray-500 hover:text-red-400 text-xl leading-none"
                      >
                        ×
                      </button>
                    </div>
                  </div>
                );
              })()}
              {/* Coverage equation pill (SF/LF only) */}
              {item.isCoverage && (
                <div className="flex items-center gap-1.5 px-2 py-1.5 bg-blue-900/30 border border-blue-700/40 rounded-lg text-xs text-blue-300 font-mono">
                  <span className="font-semibold">
                    {parseFloat(item.coveragePerUnit) || 1} {item.uom}/PC
                  </span>
                  <span className="text-blue-500">×</span>
                  <span>
                    ${fmt(item.ratePerUnit)}/{item.uom}
                  </span>
                  <span className="text-blue-500">=</span>
                  <span className="font-bold text-blue-200">
                    ${fmt(item.unitPrice)}/PC
                  </span>
                </div>
              )}

              {/* Row 1: Size · Coverage · Rate · Package cost */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {/* Size */}
                <div>
                  <label className="text-[10px] text-gray-400 mb-0.5 block">
                    {item.isCoverage
                      ? "Size (auto-calculates coverage)"
                      : "Size (style)"}
                  </label>
                  <input
                    type="text"
                    value={item.size}
                    placeholder={
                      item.isCoverage
                        ? item.uom?.toUpperCase().includes("SF")
                          ? "e.g. 4'x8' or 48\"x96\""
                          : "e.g. 8' or 15' 2\""
                        : "e.g. 4'x8'"
                    }
                    onChange={(e) => updateItem(idx, "size", e.target.value)}
                    className={cls(
                      "w-full px-2 py-1.5 text-xs rounded-lg text-gray-200 placeholder-gray-600 focus:outline-none",
                      item.isCoverage
                        ? parseSizeCoverage(item.size, item.uom) !== null
                          ? "bg-gray-600 border border-green-600/50 focus:border-orange-500"
                          : "bg-gray-600 border border-orange-600/50 focus:border-orange-500"
                        : "bg-gray-600 border border-gray-500 focus:border-orange-500",
                    )}
                  />
                  {item.isCoverage &&
                    item.size &&
                    parseSizeCoverage(item.size, item.uom) === null && (
                      <p className="text-[9px] text-orange-400 mt-0.5">
                        Can't parse — coverage unchanged
                      </p>
                    )}
                </div>

                {/* Coverage */}
                {item.isCoverage ? (
                  <div>
                    <label className="text-[10px] text-gray-400 mb-0.5 block">
                      Coverage ({item.uom}/PC) — auto
                    </label>
                    {(() => {
                      const parsed = parseSizeCoverage(item.size, item.uom);
                      const isParsed = parsed !== null;
                      return (
                        <div
                          className={cls(
                            "px-2 py-1.5 text-xs rounded-lg font-mono font-semibold border",
                            isParsed
                              ? "bg-blue-900/30 border-blue-700/40 text-blue-200"
                              : "bg-gray-600/50 border-gray-600 text-gray-400",
                          )}
                        >
                          {isParsed
                            ? `${item.coveragePerUnit} ${item.uom}`
                            : "Enter size above"}
                        </div>
                      );
                    })()}
                    <p className="text-[9px] text-gray-500 mt-0.5">
                      Calculated from size
                    </p>
                  </div>
                ) : (
                  <div>
                    <label className="text-[10px] text-gray-400 mb-0.5 block">
                      Coverage
                    </label>
                    <div className="px-2 py-1.5 text-xs bg-gray-600/50 border border-gray-600 rounded-lg text-gray-500">
                      1 (fixed)
                    </div>
                  </div>
                )}

                {/* Rate per unit */}
                <div>
                  <label className="text-[10px] text-gray-400 mb-0.5 block">
                    {item.isCoverage ? `Rate (/${item.uom})` : "Unit Price"}
                  </label>
                  <div className="relative">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs">
                      $
                    </span>
                    <input
                      type="number"
                      value={item.ratePerUnit}
                      min="0"
                      step="0.01"
                      onWheel={(e) => e.target.blur()}
                      onChange={(e) =>
                        updateItem(idx, "ratePerUnit", e.target.value)
                      }
                      className="w-full pl-5 pr-2 py-1.5 text-xs bg-gray-600 border border-gray-500 rounded-lg text-gray-200 focus:border-orange-500 focus:outline-none"
                    />
                  </div>
                </div>

                {/* Package cost */}
                <div>
                  <label className="text-[10px] text-gray-400 mb-0.5 block">
                    {item.isCoverage
                      ? "Package Cost ($/PC)"
                      : "Unit Price ($/PC)"}
                  </label>
                  {item.isCoverage ? (
                    <div className="px-2 py-1.5 text-xs bg-gray-600/50 border border-blue-700/40 rounded-lg text-blue-200 font-mono font-semibold">
                      ${fmt(item.unitPrice)}
                    </div>
                  ) : (
                    <div className="relative">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs">
                        $
                      </span>
                      <input
                        type="number"
                        value={item.unitPrice}
                        min="0"
                        step="0.01"
                        onWheel={(e) => e.target.blur()}
                        onChange={(e) =>
                          updateItem(idx, "unitPrice", e.target.value)
                        }
                        className="w-full pl-5 pr-2 py-1.5 text-xs bg-gray-600 border border-gray-500 rounded-lg text-gray-200 focus:border-orange-500 focus:outline-none"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Row 2: Qty · Vendor · Line total */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <div>
                  <label className="text-[10px] text-gray-400 mb-0.5 block">
                    Qty (PC)
                  </label>
                  <input
                    type="number"
                    value={item.quantity}
                    min="0"
                    onWheel={(e) => e.target.blur()}
                    onChange={(e) =>
                      updateItem(idx, "quantity", e.target.value)
                    }
                    className="w-full px-2 py-1.5 text-xs bg-gray-600 border border-gray-500 rounded-lg text-gray-200 focus:border-orange-500 focus:outline-none"
                  />
                </div>

                <div className="col-span-2">
                  <div className="flex items-center justify-between mb-0.5">
                    <label className="text-[10px] text-gray-400 block">
                      Vendor
                    </label>
                    {(() => {
                      const itemCode = (item.itemCode || "").trim();
                      const historyVendorIds = new Set(
                        Object.keys(priceMap?.[itemCode] || {}),
                      );
                      const dbVendorIds = new Set(
                        (item.vendorPrices || []).map((vp) =>
                          String(vp.vendor?._id || vp.vendor),
                        ),
                      );
                      const availableCount = vendors.filter(
                        (v) =>
                          historyVendorIds.has(String(v._id)) ||
                          dbVendorIds.has(String(v._id)),
                      ).length;
                      const showAll =
                        item.showAllVendors || availableCount === 0;

                      return (
                        <div className="flex rounded-md overflow-hidden border border-gray-600 text-[9px] font-medium">
                          <button
                            type="button"
                            onClick={() =>
                              updateItem(idx, "showAllVendors", false)
                            }
                            disabled={availableCount === 0}
                            className={cls(
                              "px-2 py-0.5 transition-colors",
                              !showAll
                                ? "bg-teal-600 text-white"
                                : availableCount === 0
                                  ? "bg-gray-800 text-gray-600 cursor-not-allowed"
                                  : "bg-gray-800 text-gray-400 hover:text-white hover:bg-gray-700",
                            )}
                          >
                            Priced ({availableCount})
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              updateItem(idx, "showAllVendors", true)
                            }
                            className={cls(
                              "px-2 py-0.5 transition-colors border-l border-gray-600",
                              showAll
                                ? "bg-orange-500 text-white"
                                : "bg-gray-800 text-gray-400 hover:text-white hover:bg-gray-700",
                            )}
                          >
                            All ({vendors.length})
                          </button>
                        </div>
                      );
                    })()}
                  </div>

                  {(() => {
                    const itemCode = (item.itemCode || "").trim();

                    // Build vendorId -> { rate, packageCost, source, orderNumber, partNumber }
                    const priceByVendor = {};

                    Object.entries(priceMap?.[itemCode] || {}).forEach(
                      ([vid, hist]) => {
                        const histCoverage =
                          hist.coveragePerUnit || item.coveragePerUnit || 1;
                        const rate = item.isCoverage
                          ? (hist.unitPrice || 0) / histCoverage
                          : hist.unitPrice || 0;
                        priceByVendor[vid] = {
                          rate,
                          packageCost: hist.unitPrice || 0,
                          source: "history",
                          orderNumber: hist.orderNumber,
                        };
                      },
                    );

                    (item.vendorPrices || []).forEach((vp) => {
                      const vid = String(vp.vendor?._id || vp.vendor);
                      if (priceByVendor[vid]) return;
                      const rate = vp.price || vp.unitRate || 0;
                      priceByVendor[vid] = {
                        rate,
                        packageCost: item.isCoverage
                          ? calcPackageCost(rate, item.coveragePerUnit)
                          : rate,
                        source: "db",
                        partNumber: vp.partNumber,
                      };
                    });

                    const pricedVendorIds = new Set(Object.keys(priceByVendor));
                    const availableVendors = vendors.filter((v) =>
                      pricedVendorIds.has(String(v._id)),
                    );

                    const showAll =
                      item.showAllVendors || availableVendors.length === 0;
                    const listToShow = showAll ? vendors : availableVendors;

                    const currentCost = parseFloat(item.unitPrice) || 0;

                    // Build react-select options, sorted cheapest-first among priced vendors
                    const options = [
                      { value: "", label: "Unassigned", info: null },
                      ...[...listToShow]
                        .sort((a, b) => {
                          const aInfo = priceByVendor[String(a._id)];
                          const bInfo = priceByVendor[String(b._id)];
                          if (aInfo && !bInfo) return -1;
                          if (!aInfo && bInfo) return 1;
                          if (aInfo && bInfo)
                            return aInfo.packageCost - bInfo.packageCost;
                          return a.companyName.localeCompare(b.companyName);
                        })
                        .map((v) => ({
                          value: String(v._id),
                          label: v.companyName,
                          info: priceByVendor[String(v._id)] || null,
                        })),
                    ];

                    // Ensure the currently-assigned vendor is always selectable even if
                    // it fell outside the active filter (e.g. stale vendor, no price yet).
                    if (
                      item.vendor &&
                      !options.some((o) => o.value === String(item.vendor))
                    ) {
                      const v = vendors.find(
                        (v) => String(v._id) === String(item.vendor),
                      );
                      if (v) {
                        options.push({
                          value: String(v._id),
                          label: v.companyName,
                          info: null,
                        });
                      }
                    }

                    const selectedOption =
                      options.find(
                        (o) => o.value === String(item.vendor || ""),
                      ) || null;

                    const formatOptionLabel = (opt, { context }) => {
                      if (!opt.value) return <span>{opt.label}</span>;

                      const info = opt.info;
                      const isCurrent =
                        String(opt.value) === String(item.vendor || "");

                      // In the closed control (selected value shown), just show the name —
                      // no price/delta clutter once it's chosen.
                      if (context === "value") {
                        return <span>{opt.label}</span>;
                      }

                      // In the open menu: show price + delta, but ONLY for vendors
                      // different from the one currently selected.
                      if (!info) {
                        return (
                          <div className="flex items-center justify-between gap-2">
                            <span>{opt.label}</span>
                            <span className="text-yellow-400 text-[10px]">
                              ⚠ no price
                            </span>
                          </div>
                        );
                      }

                      let deltaNode = null;
                      if (
                        !isCurrent &&
                        currentCost > 0 &&
                        info.packageCost > 0
                      ) {
                        const diffPct =
                          ((info.packageCost - currentCost) / currentCost) *
                          100;
                        if (Math.abs(diffPct) < 0.5) {
                          deltaNode = (
                            <span className="text-gray-400 text-[10px]">
                              same
                            </span>
                          );
                        } else if (diffPct < 0) {
                          deltaNode = (
                            <span className="text-green-400 text-[10px] font-semibold">
                              ▼{Math.abs(diffPct).toFixed(0)}%
                            </span>
                          );
                        } else {
                          deltaNode = (
                            <span className="text-red-400 text-[10px] font-semibold">
                              ▲{diffPct.toFixed(0)}%
                            </span>
                          );
                        }
                      }

                      return (
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-1.5 min-w-0">
                            <span className="truncate">{opt.label}</span>
                            {info.source === "history" && (
                              <span className="text-[9px] px-1 py-0.5 rounded bg-blue-900/50 text-blue-300 border border-blue-700/40 shrink-0">
                                ↩ MO#{info.orderNumber}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-1.5 shrink-0">
                            <span className="font-mono text-gray-300">
                              ${fmt(info.packageCost)}
                            </span>
                            {deltaNode}
                          </div>
                        </div>
                      );
                    };

                    const selectStyles = {
                      control: (base, state) => ({
                        ...base,
                        minHeight: "30px",
                        backgroundColor: "rgb(75 85 99)", // gray-600
                        borderColor:
                          item.vendorPriceStatus === "missing"
                            ? "rgba(202 138 4 / 0.6)"
                            : item.vendorPriceStatus === "found"
                              ? "rgba(22 163 74 / 0.5)"
                              : "rgb(107 114 128)",
                        boxShadow: "none",
                        "&:hover": { borderColor: "rgb(249 115 22)" },
                        fontSize: "0.75rem",
                      }),
                      valueContainer: (base) => ({ ...base, padding: "0 8px" }),
                      input: (base) => ({
                        ...base,
                        color: "rgb(229 231 235)",
                        margin: 0,
                      }),
                      singleValue: (base) => ({
                        ...base,
                        color: "rgb(229 231 235)",
                      }),
                      placeholder: (base) => ({
                        ...base,
                        color: "rgb(156 163 175)",
                      }),
                      menu: (base) => ({
                        ...base,
                        backgroundColor: "rgb(31 41 55)", // gray-800
                        border: "1px solid rgb(75 85 99)",
                        zIndex: 50,
                      }),
                      menuList: (base) => ({ ...base, padding: "4px" }),
                      option: (base, state) => ({
                        ...base,
                        borderRadius: "6px",
                        padding: "6px 8px",
                        fontSize: "0.75rem",
                        backgroundColor: state.isSelected
                          ? "rgba(249 115 22 / 0.25)"
                          : state.isFocused
                            ? "rgb(55 65 81)"
                            : "transparent",
                        color: "rgb(229 231 235)",
                        cursor: "pointer",
                      }),
                      indicatorSeparator: () => ({ display: "none" }),
                      dropdownIndicator: (base) => ({
                        ...base,
                        color: "rgb(156 163 175)",
                        padding: "0 6px",
                      }),
                      clearIndicator: (base) => ({
                        ...base,
                        color: "rgb(156 163 175)",
                        padding: "0 4px",
                      }),
                    };

                    return (
                      <>
                        <div className="flex items-center gap-1.5">
                          <div className="flex-1 min-w-0">
                            <Select
                              value={selectedOption}
                              onChange={(opt) =>
                                updateItem(idx, "vendor", opt?.value || "")
                              }
                              options={options}
                              formatOptionLabel={formatOptionLabel}
                              styles={selectStyles}
                              isClearable
                              isSearchable
                              placeholder="Select vendor…"
                              classNamePrefix="vendor-select"
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => loadLatestPrice(idx)}
                            disabled={!hasAnyLatestPrice(item)}
                            title={(() => {
                              const latest = findLatestOrderForItem(
                                item.itemCode,
                              );
                              if (!latest)
                                return "No order history found for this item";
                              const vendorName =
                                vendors.find(
                                  (v) =>
                                    String(v._id) === String(latest.vendorId),
                                )?.companyName || "Unknown vendor";
                              return `Load latest: ${vendorName} · $${fmt(
                                latest.hist.unitPrice,
                              )} · MO #${latest.hist.orderNumber ?? "—"}`;
                            })()}
                            className={cls(
                              "shrink-0 px-2 py-1.5 text-[10px] font-semibold rounded-lg border transition-colors",
                              hasAnyLatestPrice(item)
                                ? "bg-blue-600/70 hover:bg-blue-500 text-blue-50 border-blue-500/60"
                                : "bg-gray-700 text-gray-600 border-gray-600 cursor-not-allowed",
                            )}
                          >
                            ↩ Latest
                          </button>
                          <button
                            type="button"
                            onClick={() => loadLatestPriceForVendor(idx)}
                            disabled={
                              !item.vendor || !hasLatestPriceForVendor(item)
                            }
                            title={(() => {
                              if (!item.vendor) return "Select a vendor first";
                              const found = findLatestOrderForVendor(
                                item.itemCode,
                                item.vendor,
                              );
                              if (!found)
                                return "No order history with this vendor for this item";
                              const vendorName =
                                vendors.find(
                                  (v) => String(v._id) === String(item.vendor),
                                )?.companyName || "this vendor";
                              return `Load latest from ${vendorName}: $${fmt(
                                found.hist.unitPrice,
                              )} · MO #${found.hist.orderNumber ?? "—"}`;
                            })()}
                            className={cls(
                              "shrink-0 px-2 py-1.5 text-[10px] font-semibold rounded-lg border transition-colors",
                              item.vendor && hasLatestPriceForVendor(item)
                                ? "bg-purple-600/70 hover:bg-purple-500 text-purple-50 border-purple-500/60"
                                : "bg-gray-700 text-gray-600 border-gray-600 cursor-not-allowed",
                            )}
                          >
                            ↻ Vendor Latest
                          </button>
                        </div>

                        {availableVendors.length === 0 &&
                          !item.showAllVendors && (
                            <p className="text-[9px] text-gray-500 mt-0.5">
                              No vendors have pricing for this item yet —
                              showing all vendors.
                            </p>
                          )}
                      </>
                    );
                  })()}

                  {/* Price source detail */}
                  {item.vendor && item.vendorPriceStatus === "found" && (
                    <p className="text-[9px] text-green-400 mt-0.5">
                      ✓{" "}
                      {item.priceSource?.startsWith("MO #")
                        ? `Price from ${item.priceSource} · `
                        : "Price on file · "}
                      ${fmt(item.ratePerUnit)}/{item.uom}
                      {(item.vendorPrices || []).find(
                        (vp) =>
                          (vp.vendor?._id || vp.vendor)?.toString() ===
                          item.vendor.toString(),
                      )?.partNumber && (
                        <span className="text-gray-500 ml-1">
                          ·{" "}
                          {
                            item.vendorPrices.find(
                              (vp) =>
                                (vp.vendor?._id || vp.vendor)?.toString() ===
                                item.vendor.toString(),
                            ).partNumber
                          }
                        </span>
                      )}
                    </p>
                  )}
                  {item.vendor &&
                    item.vendorPriceStatus === "missing" &&
                    !item.addingVendorPrice && (
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <p className="text-[9px] text-yellow-400">
                          ⚠ No price on file
                        </p>
                        <button
                          type="button"
                          onClick={() =>
                            updateItem(idx, "addingVendorPrice", true)
                          }
                          className="text-[9px] text-orange-400 hover:text-orange-300 underline"
                        >
                          Add price
                        </button>
                      </div>
                    )}
                </div>

                <div className="flex flex-col justify-end">
                  <label className="text-[10px] text-gray-400 mb-0.5 block">
                    Line Total
                  </label>
                  <div className="px-2 py-1.5 text-xs bg-gray-600/50 border border-gray-600 rounded-lg text-green-300 font-mono font-semibold text-right">
                    $
                    {fmt(
                      (parseFloat(item.quantity) || 0) *
                        (parseFloat(item.unitPrice) || 0),
                    )}
                  </div>
                </div>
              </div>

              {/* Add vendor price inline form */}
              {item.addingVendorPrice && (
                <div className="bg-yellow-950/20 border border-yellow-700/40 rounded-lg p-3 space-y-2">
                  <p className="text-[10px] text-yellow-300 font-medium">
                    Add price for{" "}
                    {vendors.find((v) => v._id === item.vendor)?.companyName ||
                      "this vendor"}{" "}
                    <span className="text-gray-500">
                      — will be saved to item database
                    </span>
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[9px] text-gray-400 mb-0.5 block">
                        Price (/{item.uom}) *
                      </label>
                      <div className="relative">
                        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs">
                          $
                        </span>
                        <input
                          type="number"
                          value={item.newVendorPriceInput}
                          min="0"
                          step="0.01"
                          placeholder="0.00"
                          onWheel={(e) => e.target.blur()}
                          onChange={(e) =>
                            updateItem(
                              idx,
                              "newVendorPriceInput",
                              e.target.value,
                            )
                          }
                          className="w-full pl-5 pr-2 py-1.5 text-xs bg-gray-700 border border-yellow-700/50 rounded text-gray-200 focus:border-yellow-500 focus:outline-none"
                          autoFocus
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-[9px] text-gray-400 mb-0.5 block">
                        Vendor Part # (optional)
                      </label>
                      <input
                        type="text"
                        value={item.newVendorPartNumber}
                        placeholder="e.g. DW-5812"
                        onChange={(e) =>
                          updateItem(idx, "newVendorPartNumber", e.target.value)
                        }
                        className="w-full px-2 py-1.5 text-xs bg-gray-700 border border-gray-600 rounded text-gray-200 focus:border-orange-500 focus:outline-none"
                      />
                    </div>
                  </div>
                  {item.newVendorPriceInput &&
                    !isNaN(parseFloat(item.newVendorPriceInput)) &&
                    parseFloat(item.newVendorPriceInput) > 0 && (
                      <p className="text-[9px] text-blue-300 font-mono">
                        Preview package cost:{" "}
                        <span className="font-bold">
                          $
                          {fmt(
                            calcPackageCost(
                              item.newVendorPriceInput,
                              item.isCoverage ? item.coveragePerUnit : 1,
                            ),
                          )}
                          /PC
                        </span>
                        {item.isCoverage && (
                          <span className="text-gray-500 ml-1">
                            ({item.coveragePerUnit} {item.uom} × $
                            {fmt(item.newVendorPriceInput)}/{item.uom})
                          </span>
                        )}
                      </p>
                    )}
                  {item.vendorPriceError && (
                    <p className="text-[9px] text-red-400">
                      {item.vendorPriceError}
                    </p>
                  )}
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => saveVendorPrice(idx)}
                      disabled={
                        item.vendorPriceSaving || !item.newVendorPriceInput
                      }
                      className={cls(
                        "px-3 py-1 text-xs font-semibold rounded text-white transition-colors",
                        item.vendorPriceSaving || !item.newVendorPriceInput
                          ? "bg-gray-600 cursor-not-allowed"
                          : "bg-yellow-600 hover:bg-yellow-500",
                      )}
                    >
                      {item.vendorPriceSaving ? "Saving…" : "Save & Apply"}
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        updateItem(idx, "addingVendorPrice", false)
                      }
                      className="px-3 py-1 text-xs text-gray-400 hover:text-white rounded border border-gray-600"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              <input
                type="text"
                value={item.notes}
                placeholder="Item notes…"
                onChange={(e) => updateItem(idx, "notes", e.target.value)}
                className="w-full px-2 py-1.5 text-xs bg-gray-600/50 border border-gray-600 rounded-lg text-gray-400 placeholder-gray-600 focus:border-orange-500 focus:outline-none"
              />
            </div>
          ))}
        </div>
      </div>

      {selectedPO && items.length > 0 && (
        <>
          {isMultiVendor ? (
            <div className="space-y-3">
              <p className="text-xs text-gray-400 font-medium">
                Delivery Details —{" "}
                <span className="text-orange-400">
                  {uniqueVendorIds.length} vendors
                </span>{" "}
                detected. Set delivery options for each.
              </p>
              {uniqueVendorIds.map((vid) => {
                const vendor = vendors.find((v) => v._id === vid);
                const vl = vendorLogistics[vid] || {};
                const isPickup = (vl.shippingMethod || "Delivery") === "Pickup";
                return (
                  <div
                    key={vid}
                    className="bg-gray-700/60 rounded-xl border border-gray-600 p-3 space-y-2.5"
                  >
                    <p className="text-xs font-semibold text-orange-400">
                      {vendor?.companyName || "Unknown Vendor"}
                    </p>
                    <div
                      className={cls(
                        "grid gap-2",
                        isPickup
                          ? "grid-cols-1 sm:grid-cols-3"
                          : "grid-cols-1 sm:grid-cols-2",
                      )}
                    >
                      <div>
                        <label className="text-[10px] text-gray-400 mb-0.5 block">
                          Shipping
                        </label>
                        <select
                          value={vl.shippingMethod || "Delivery"}
                          onChange={(e) =>
                            updateVendorLogistic(
                              vid,
                              "shippingMethod",
                              e.target.value,
                            )
                          }
                          className="w-full px-2 py-1.5 text-xs bg-gray-600 border border-gray-500 rounded-lg text-gray-200 focus:border-orange-500 focus:outline-none"
                        >
                          <option>Delivery</option>
                          <option>Pickup</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-[10px] text-gray-400 mb-0.5 block">
                          Expected Delivery
                        </label>
                        <input
                          type="date"
                          value={vl.expectedDeliveryDate || getTomorrow()}
                          onChange={(e) =>
                            updateVendorLogistic(
                              vid,
                              "expectedDeliveryDate",
                              e.target.value,
                            )
                          }
                          className="w-full px-2 py-1.5 text-xs bg-gray-600 border border-gray-500 rounded-lg text-gray-200 focus:border-orange-500 focus:outline-none"
                        />
                      </div>
                      {isPickup && (
                        <div>
                          <label className="text-[10px] text-gray-400 mb-0.5 block">
                            Driver
                          </label>
                          <select
                            value={vl.deliveryContact || ""}
                            onChange={(e) =>
                              updateVendorLogistic(
                                vid,
                                "deliveryContact",
                                e.target.value,
                              )
                            }
                            className="w-full px-2 py-1.5 text-xs bg-gray-600 border border-gray-500 rounded-lg text-gray-200 focus:border-orange-500 focus:outline-none"
                          >
                            <option value="">Select driver…</option>
                            {users.map((u) => (
                              <option key={u._id} value={u._id}>
                                {u.displayName}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="text-[10px] text-gray-400 mb-0.5 block">
                        Delivery Instructions
                      </label>
                      <textarea
                        value={vl.deliveryInstructions || ""}
                        rows={2}
                        onChange={(e) =>
                          updateVendorLogistic(
                            vid,
                            "deliveryInstructions",
                            e.target.value,
                          )
                        }
                        placeholder="Access instructions, floor, loading dock…"
                        className="w-full px-2 py-1.5 text-xs bg-gray-600 border border-gray-500 rounded-lg text-gray-200 placeholder-gray-500 focus:border-orange-500 focus:outline-none resize-none"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] text-gray-400 mb-0.5 block">
                        Notes
                      </label>
                      <textarea
                        value={vl.notes || ""}
                        rows={2}
                        onChange={(e) =>
                          updateVendorLogistic(vid, "notes", e.target.value)
                        }
                        placeholder="Internal notes for this order…"
                        className="w-full px-2 py-1.5 text-xs bg-gray-600 border border-gray-500 rounded-lg text-gray-200 placeholder-gray-500 focus:border-orange-500 focus:outline-none resize-none"
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div
              className={cls(
                "grid gap-3",
                logistics.shippingMethod === "Pickup"
                  ? "grid-cols-1 sm:grid-cols-3"
                  : "grid-cols-1 sm:grid-cols-2",
              )}
            >
              <div>
                <label className="text-xs text-gray-400 mb-1 block">
                  Shipping Method
                </label>
                <select
                  value={logistics.shippingMethod}
                  onChange={(e) =>
                    setLogistics((l) => ({
                      ...l,
                      shippingMethod: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 text-sm bg-gray-700 border border-gray-600 rounded-lg text-gray-200 focus:border-orange-500 focus:outline-none"
                >
                  <option>Delivery</option>
                  <option>Pickup</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-400 mb-1 block">
                  Expected Delivery
                </label>
                <input
                  type="date"
                  value={logistics.expectedDeliveryDate}
                  onChange={(e) =>
                    setLogistics((l) => ({
                      ...l,
                      expectedDeliveryDate: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 text-sm bg-gray-700 border border-gray-600 rounded-lg text-gray-200 focus:border-orange-500 focus:outline-none"
                />
              </div>
              {logistics.shippingMethod === "Pickup" && (
                <div>
                  <label className="text-xs text-gray-400 mb-1 block">
                    Delivery Driver
                  </label>
                  <select
                    value={logistics.deliveryContact || ""}
                    onChange={(e) =>
                      setLogistics((l) => ({
                        ...l,
                        deliveryContact: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 text-sm bg-gray-700 border border-gray-600 rounded-lg text-gray-200 focus:border-orange-500 focus:outline-none"
                  >
                    <option value="">Select driver…</option>
                    {users.map((u) => (
                      <option key={u._id} value={u._id}>
                        {u.displayName}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          )}

          {/* Delivery instructions + notes: single-vendor only (multi-vendor has them per-card) */}
          {!isMultiVendor && (
            <>
              <div>
                <label className="text-xs text-gray-400 mb-1 block">
                  Delivery Instructions
                </label>
                <textarea
                  value={logistics.deliveryInstructions}
                  rows={2}
                  onChange={(e) =>
                    setLogistics((l) => ({
                      ...l,
                      deliveryInstructions: e.target.value,
                    }))
                  }
                  placeholder="Access instructions, floor, loading dock…"
                  className="w-full px-3 py-2 text-sm bg-gray-700 border border-gray-600 rounded-lg text-gray-200 placeholder-gray-500 focus:border-orange-500 focus:outline-none resize-none"
                />
              </div>
              <div>
                <label className="text-xs text-gray-400 mb-1 block">
                  Notes
                </label>
                <textarea
                  value={logistics.notes}
                  rows={2}
                  onChange={(e) =>
                    setLogistics((l) => ({ ...l, notes: e.target.value }))
                  }
                  placeholder="Internal notes…"
                  className="w-full px-3 py-2 text-sm bg-gray-700 border border-gray-600 rounded-lg text-gray-200 placeholder-gray-500 focus:border-orange-500 focus:outline-none resize-none"
                />
              </div>
            </>
          )}
        </>
      )}

      <BudgetImpactPanel
        poId={selectedPO}
        token={token}
        orderRows={budgetOrderRows}
        title="Budget impact of this request"
      />

      <div className="flex gap-3 pt-2">
        <button
          onClick={handleSubmit}
          disabled={saving}
          className={cls(
            "px-5 py-2 text-sm font-semibold rounded-lg text-white transition-colors",
            saving
              ? "bg-gray-600 cursor-not-allowed"
              : "bg-orange-500 hover:bg-orange-600",
          )}
        >
          {saving
            ? isEditing
              ? "Saving…"
              : "Creating…"
            : isEditing
              ? "Save Changes"
              : "Create Request"}
        </button>
        <button
          onClick={onCancel}
          className="px-4 py-2 text-sm text-gray-400 hover:text-white rounded-lg border border-gray-600 hover:border-gray-400"
        >
          Cancel
        </button>
      </div>

      {showSearch && (
        <ItemSearchHelper3
          token={token}
          purchaseOrder={selectedPO}
          recommendedBudgetItems={recommendedBudgetItems}
          onSelect={(item, qtyOverride) => addItem(item, qtyOverride)}
          onClose={() => setShowSearch(false)}
          title="Add Items to Request"
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// RequestDetail — view + generate/cancel, with Edit button for drafts
// ─────────────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
// RequestDetail — view + generate/cancel, with Edit button for drafts
// ─────────────────────────────────────────────────────────────────────────────
function RequestDetail({
  request,
  token,
  priceMap,
  vendors,
  purchaseOrders,
  users,
  projectId,
  onGenerated,
  onCancelled,
  onUpdated,
  onDuplicated,
  onClose,
}) {
  const [editing, setEditing] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const [error, setError] = useState("");

  // Pricing/catalog-match info for unassigned items, keyed by item index.
  //   { count, source: "history" | "database" }
  //   { catalogMatch: true, itemDatabaseId, count }
  //   { lookupFailed: true }
  const [unassignedInfo, setUnassignedInfo] = useState({});

  const canEdit = ["draft", "submitted"].includes(request.status);
  const canGenerate = ["draft", "submitted"].includes(request.status);
  const canCancel = ["draft", "submitted"].includes(request.status);

  const uniqueVendors = new Set(
    (request.items || []).map(
      (i) => i.vendor?._id || i.vendor?.toString() || "__none__",
    ),
  ).size;
  const total = (request.items || []).reduce(
    (s, i) =>
      s + (parseFloat(i.quantity) || 0) * (parseFloat(i.unitPrice) || 0),
    0,
  );

  useEffect(() => {
    let cancelled = false;

    const check = async () => {
      const items = request.items || [];
      const results = {};

      await Promise.all(
        items.map(async (item, idx) => {
          const hasVendor =
            item.vendor?._id ||
            (typeof item.vendor === "string" && item.vendor);
          if (hasVendor) return;

          const itemCode = (item.itemCode || "").trim();

          // 1. Project order history for this itemCode
          const historyVendorIds = Object.keys(priceMap?.[itemCode] || {});
          if (historyVendorIds.length > 0) {
            results[idx] = {
              count: historyVendorIds.length,
              source: "history",
            };
            return;
          }

          // 2. If linked, check the item-database record directly
          if (item.itemDatabaseId) {
            try {
              const res = await axios.get(
                `${API}/item-database/${item.itemDatabaseId}`,
                { headers: { Authorization: `Bearer ${token}` } },
              );
              const dbItem = res.data.item || res.data;
              const count = (dbItem.vendorPrices || []).length;
              if (count > 0) {
                results[idx] = { count, source: "database" };
                return;
              }
            } catch (err) {
              console.warn(
                `[unassignedInfo] itemDatabaseId lookup failed for ${itemCode}:`,
                err?.response?.status,
                err?.message,
              );
            }
          }

          // 3. NOT linked — search catalog by itemCode
          if (!item.itemDatabaseId && itemCode) {
            try {
              const res = await axios.get(
                `${API}/item-database/code/${encodeURIComponent(itemCode)}`,
                { headers: { Authorization: `Bearer ${token}` } },
              );
              const exact = res.data.item;
              if (exact) {
                const count = (exact.vendorPrices || []).length;
                results[idx] = {
                  catalogMatch: true,
                  itemDatabaseId: exact._id,
                  count,
                };
              }
            } catch (err) {
              if (err?.response?.status !== 404) {
                console.warn(
                  `[unassignedInfo] code lookup failed for ${itemCode} (not a 404, treating as unknown, not custom):`,
                  err?.response?.status,
                  err?.message,
                );
                results[idx] = { lookupFailed: true };
                return;
              }
            }
          }
        }),
      );

      if (!cancelled) setUnassignedInfo(results);
    };

    check();
    return () => {
      cancelled = true;
    };
  }, [request, priceMap, token]);

  const handleGenerate = async () => {
    setGenerating(true);
    setError("");
    try {
      const res = await axios.post(
        `${API}/materialRequests3/${request._id}/generate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      onGenerated(res.data.request, res.data.orderCount);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setGenerating(false);
    }
  };

  const handleCancel = async () => {
    setCancelling(true);
    setError("");
    try {
      await axios.delete(`${API}/materialRequests3/${request._id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      onCancelled(request._id);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setCancelling(false);
    }
  };

  const handleDuplicate = async () => {
    setDuplicating(true);
    setError("");
    try {
      const res = await axios.post(
        `${API}/materialRequests3/${request._id}/duplicate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      onDuplicated(res.data.request);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setDuplicating(false);
    }
  };

  const vendorGroups = useMemo(() => {
    const groups = new Map();
    (request.items || []).forEach((item, itemIdx) => {
      const vid =
        item.vendor?._id?.toString() ||
        (typeof item.vendor === "string" ? item.vendor : "") ||
        "__none__";
      if (!groups.has(vid)) {
        const populatedName =
          item.vendor && typeof item.vendor === "object"
            ? item.vendor.companyName
            : null;
        const vendorObj = vendors.find(
          (v) => v._id?.toString() === vid || v._id === vid,
        );
        const logEntry = (request.vendorLogistics || []).find((vl) => {
          const vlVid =
            vl.vendor?._id?.toString() ||
            (typeof vl.vendor === "string" ? vl.vendor : "");
          return vlVid === vid;
        });
        const driverUser = logEntry?.deliveryContact
          ? users.find(
              (u) =>
                u._id?.toString() ===
                (logEntry.deliveryContact?._id?.toString() ||
                  logEntry.deliveryContact?.toString()),
            )
          : null;
        groups.set(vid, {
          vendorId: vid,
          vendorName:
            populatedName ||
            vendorObj?.companyName ||
            (vid === "__none__" ? "Unassigned" : "Unknown Vendor"),
          items: [],
          shippingMethod:
            logEntry?.shippingMethod || request.shippingMethod || "Delivery",
          expectedDeliveryDate:
            logEntry?.expectedDeliveryDate || request.expectedDeliveryDate,
          deliveryContactName: driverUser?.displayName || null,
          deliveryInstructions:
            logEntry?.deliveryInstructions ||
            request.deliveryInstructions ||
            "",
          // `||` (not `??`) so an empty per-vendor note falls back to the request note
          notes: logEntry?.notes || request.notes || "",
        });
      }
      groups.get(vid).items.push({ ...item, __origIdx: itemIdx });
    });
    return [...groups.values()];
  }, [request, vendors, users]);

  const isMultiVendorRequest = vendorGroups.length > 1;

  const VendorCell = ({ item, origIdx }) => {
    const name = item.vendor?.companyName;
    if (name) return <>{name}</>;

    const info = unassignedInfo[origIdx];

    if (info?.catalogMatch) {
      return (
        <span className="inline-flex items-center gap-1">
          <span>Unassigned</span>
          <span
            className="text-[9px] px-1.5 py-0.5 rounded font-medium bg-orange-900/40 text-orange-300 border border-orange-700/40"
            title={
              info.count > 0
                ? `Item code "${item.itemCode}" exists in the catalog with ${info.count} vendor price(s), but this line wasn't linked to it — edit to relink and assign a vendor.`
                : `Item code "${item.itemCode}" exists in the catalog, but has no vendor pricing yet — edit to relink and add pricing.`
            }
          >
            ⚠ catalog match — not linked
          </span>
        </span>
      );
    }

    if (info?.count > 0) {
      return (
        <span className="inline-flex items-center gap-1">
          <span>Unassigned</span>
          <span
            className="text-[9px] px-1.5 py-0.5 rounded font-medium bg-yellow-900/40 text-yellow-300 border border-yellow-700/40"
            title={
              info.source === "history"
                ? `${info.count} vendor(s) have order history for this item — edit to assign`
                : `${info.count} vendor(s) have pricing on file — edit to assign`
            }
          >
            ⚠ {info.count} priced vendor{info.count !== 1 ? "s" : ""} available
          </span>
        </span>
      );
    }

    return (
      <span className="inline-flex items-center gap-1">
        <span>Unassigned</span>
        <span
          className="text-[9px] px-1.5 py-0.5 rounded font-medium bg-gray-700/60 text-gray-400 border border-gray-600"
          title="No matching catalog item was found for this code — this looks like a genuine custom/manual line item."
        >
          custom item
        </span>
      </span>
    );
  };

  // Internal note row shown beneath a line item
  const ItemNoteRow = ({ item, colSpan, rowBg }) =>
    item.notes?.trim() ? (
      <tr className={rowBg}>
        <td colSpan={colSpan} className="px-3 pb-2 pt-0">
          <span className="text-[9px] uppercase tracking-wide text-amber-400/80 mr-1.5">
            Internal note
          </span>
          <span className="text-xs text-gray-300">{item.notes}</span>
        </td>
      </tr>
    ) : null;

  if (editing) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-base font-semibold text-white">
              Edit Request #{request.requestNumber}
            </h4>
            <p className="text-xs text-gray-400 mt-0.5">
              PO #{request.purchaseOrder?.poNumber} —{" "}
              {request.purchaseOrder?.title}
            </p>
          </div>
          <StatusBadge status={request.status} />
        </div>
        <RequestForm
          projectId={projectId}
          purchaseOrders={purchaseOrders}
          users={users}
          token={token}
          priceMap={priceMap}
          vendors={vendors}
          existingRequest={request}
          allowPOChange={true}
          onSaved={(updated) => {
            onUpdated(updated);
            setEditing(false);
          }}
          onCancel={() => setEditing(false)}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-base font-semibold text-white">
            Request #{request.requestNumber}
          </h4>
          <p className="text-xs text-gray-400 mt-0.5">
            PO #{request.purchaseOrder?.poNumber} —{" "}
            {request.purchaseOrder?.title}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge status={request.status} />
          {canEdit && (
            <button
              onClick={() => setEditing(true)}
              className="px-3 py-1.5 text-xs font-medium bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white rounded-lg border border-gray-600 transition-colors"
            >
              ✎ Edit
            </button>
          )}
          <button
            onClick={handleDuplicate}
            disabled={duplicating}
            title="Duplicate as new draft"
            className="px-3 py-1.5 text-xs font-medium bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white rounded-lg border border-gray-600 transition-colors disabled:opacity-40"
          >
            {duplicating ? "Duplicating…" : "⧉ Duplicate"}
          </button>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-white text-xl leading-none"
          >
            ×
          </button>
        </div>
      </div>

      {error && (
        <div className="px-3 py-2 bg-red-900/50 border border-red-700 rounded-lg text-xs text-red-300">
          {error}
        </div>
      )}

      {isMultiVendorRequest ? (
        <div className="space-y-3">
          {vendorGroups.map((group) => {
            const isPickup = group.shippingMethod === "Pickup";
            const groupTotal = group.items.reduce(
              (s, i) =>
                s +
                (parseFloat(i.quantity) || 0) * (parseFloat(i.unitPrice) || 0),
              0,
            );
            return (
              <div
                key={group.vendorId}
                className="border border-gray-700 rounded-xl overflow-hidden"
              >
                <div className="bg-gray-900 px-4 py-2.5 flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-orange-400">
                      {group.vendorName}
                    </span>
                    <span className="text-[10px] text-gray-500">
                      {group.items.length} item
                      {group.items.length !== 1 ? "s" : ""}
                    </span>
                    <span className="text-[10px] font-mono text-green-400">
                      ${fmt(groupTotal)}
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[10px]">
                    <div className="flex items-center gap-1">
                      <span className="text-gray-500">Shipping:</span>
                      <span
                        className={cls(
                          "font-medium",
                          isPickup ? "text-amber-300" : "text-gray-200",
                        )}
                      >
                        {group.shippingMethod}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="text-gray-500">Expected:</span>
                      <span className="text-gray-200 font-medium">
                        {group.expectedDeliveryDate
                          ? new Date(
                              group.expectedDeliveryDate,
                            ).toLocaleDateString()
                          : "—"}
                      </span>
                    </div>
                    {isPickup && group.deliveryContactName && (
                      <div className="flex items-center gap-1">
                        <span className="text-gray-500">Driver:</span>
                        <span className="text-gray-200 font-medium">
                          {group.deliveryContactName}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-800/60">
                      <tr>
                        <th className="px-3 py-2 text-left text-gray-400">
                          Code
                        </th>
                        <th className="px-3 py-2 text-left text-gray-400 min-w-[140px]">
                          Description
                        </th>
                        <th className="px-3 py-2 text-left text-gray-400">
                          Size
                        </th>
                        <th className="px-3 py-2 text-center text-gray-400">
                          Coverage
                        </th>
                        <th className="px-3 py-2 text-right text-gray-400">
                          Qty (PC)
                        </th>
                        <th className="px-3 py-2 text-right text-gray-400">
                          $/PC
                        </th>
                        <th className="px-3 py-2 text-right text-gray-400">
                          Total
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-700/50">
                      {group.items.map((item, i) => {
                        const covered = isCoverageBased(item.uom);
                        const rowBg =
                          i % 2 === 0 ? "bg-gray-800/40" : "bg-gray-700/20";
                        return (
                          <Fragment key={i}>
                            <tr className={rowBg}>
                              <td className="px-3 py-2 font-mono text-orange-400">
                                {item.itemCode || "—"}
                              </td>
                              <td className="px-3 py-2 text-gray-200">
                                {item.description}
                              </td>
                              <td className="px-3 py-2 text-gray-400">
                                {item.size || "—"}
                              </td>
                              <td className="px-3 py-2 text-center">
                                {covered ? (
                                  <span className="text-[10px] text-blue-300 font-mono">
                                    {item.coveragePerUnit} {item.uom}/PC
                                  </span>
                                ) : (
                                  <span className="text-[10px] text-gray-500">
                                    —
                                  </span>
                                )}
                              </td>
                              <td className="px-3 py-2 text-right text-gray-300 font-mono">
                                {item.quantity}
                              </td>
                              <td className="px-3 py-2 text-right text-gray-300 font-mono">
                                ${fmt(item.unitPrice)}
                              </td>
                              <td className="px-3 py-2 text-right text-green-300 font-mono font-semibold">
                                $
                                {fmt(
                                  (item.quantity || 0) * (item.unitPrice || 0),
                                )}
                              </td>
                            </tr>
                            <ItemNoteRow
                              item={item}
                              colSpan={7}
                              rowBg={rowBg}
                            />
                          </Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {(group.deliveryInstructions || group.notes) && (
                  <div className="border-t border-gray-700/60 px-4 py-3 space-y-2 bg-gray-800/30">
                    {group.deliveryInstructions && (
                      <div>
                        <p className="text-[9px] text-gray-500 uppercase tracking-wide mb-0.5">
                          Delivery Instructions
                        </p>
                        <p className="text-xs text-gray-300">
                          {group.deliveryInstructions}
                        </p>
                      </div>
                    )}
                    {group.notes && (
                      <div>
                        <p className="text-[9px] text-gray-500 uppercase tracking-wide mb-0.5">
                          Internal Notes
                        </p>
                        <p className="text-xs text-gray-300">{group.notes}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          <div className="flex justify-between items-center px-4 py-2 bg-gray-800 rounded-lg border border-gray-700 text-xs">
            <span className="text-gray-400">
              {(request.items || []).length} items · {vendorGroups.length}{" "}
              vendors
            </span>
            <span className="font-mono font-bold text-green-300">
              ${fmt(total)}
            </span>
          </div>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto rounded-lg border border-gray-700">
            <table className="w-full text-xs">
              <thead className="bg-gray-800">
                <tr>
                  <th className="px-3 py-2 text-left text-gray-400">Code</th>
                  <th className="px-3 py-2 text-left text-gray-400 min-w-[140px]">
                    Description
                  </th>
                  <th className="px-3 py-2 text-left text-gray-400">Size</th>
                  <th className="px-3 py-2 text-center text-gray-400">
                    Coverage
                  </th>
                  <th className="px-3 py-2 text-right text-gray-400">
                    Qty (PC)
                  </th>
                  <th className="px-3 py-2 text-right text-gray-400">$/PC</th>
                  <th className="px-3 py-2 text-right text-gray-400">Total</th>
                  <th className="px-3 py-2 text-left text-gray-400">Vendor</th>
                  <th className="px-3 py-2 text-center text-gray-400">PO</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700/50">
                {(request.items || []).map((item, i) => {
                  const covered = isCoverageBased(item.uom);
                  const rowBg =
                    i % 2 === 0 ? "bg-gray-800/40" : "bg-gray-700/20";
                  return (
                    <Fragment key={i}>
                      <tr className={rowBg}>
                        <td className="px-3 py-2 font-mono text-orange-400">
                          {item.itemCode || "—"}
                        </td>
                        <td className="px-3 py-2 text-gray-200">
                          {item.description}
                        </td>
                        <td className="px-3 py-2 text-gray-400">
                          {item.size || "—"}
                        </td>
                        <td className="px-3 py-2 text-center">
                          {covered ? (
                            <span className="text-[10px] text-blue-300 font-mono">
                              {item.coveragePerUnit} {item.uom}/PC
                            </span>
                          ) : (
                            <span className="text-[10px] text-gray-500">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right text-gray-300 font-mono">
                          {item.quantity}
                        </td>
                        <td className="px-3 py-2 text-right text-gray-300 font-mono">
                          ${fmt(item.unitPrice)}
                        </td>
                        <td className="px-3 py-2 text-right text-green-300 font-mono font-semibold">
                          ${fmt((item.quantity || 0) * (item.unitPrice || 0))}
                        </td>
                        <td className="px-3 py-2 text-gray-400">
                          <VendorCell item={item} origIdx={i} />
                        </td>
                        <td className="px-3 py-2 text-center">
                          {item.isNewPoItem ? (
                            <span className="text-[9px] bg-yellow-600/60 text-yellow-100 px-1.5 py-0.5 rounded font-semibold">
                              NEW
                            </span>
                          ) : (
                            <span className="text-[9px] bg-green-600/60 text-green-100 px-1.5 py-0.5 rounded font-semibold">
                              MATCH
                            </span>
                          )}
                        </td>
                      </tr>
                      <ItemNoteRow item={item} colSpan={9} rowBg={rowBg} />
                    </Fragment>
                  );
                })}
              </tbody>
              <tfoot className="bg-gray-800 border-t border-gray-600">
                <tr>
                  <td colSpan={6} className="px-3 py-2 text-xs text-gray-400">
                    {(request.items || []).length} items · {uniqueVendors}{" "}
                    vendor
                    {uniqueVendors !== 1 ? "s" : ""}
                  </td>
                  <td className="px-3 py-2 text-right text-sm font-bold text-green-300 font-mono">
                    ${fmt(total)}
                  </td>
                  <td colSpan={2} />
                </tr>
              </tfoot>
            </table>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
            {[
              { label: "Shipping", value: request.shippingMethod },
              {
                label: "Expected",
                value: request.expectedDeliveryDate
                  ? new Date(request.expectedDeliveryDate).toLocaleDateString()
                  : "—",
              },
              {
                label: "Contact",
                value: request.deliveryContact?.displayName || "—",
              },
            ].map(({ label, value }) => (
              <div key={label} className="bg-gray-800 rounded-lg p-3">
                <p className="text-gray-500 mb-0.5">{label}</p>
                <p className="text-gray-200 font-medium">{value}</p>
              </div>
            ))}
          </div>
          {(request.deliveryInstructions || request.notes) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
              {request.deliveryInstructions && (
                <div className="bg-gray-800 rounded-lg p-3">
                  <p className="text-gray-500 mb-0.5">Delivery Instructions</p>
                  <p className="text-gray-200">
                    {request.deliveryInstructions}
                  </p>
                </div>
              )}
              {request.notes && (
                <div className="bg-gray-800 rounded-lg p-3">
                  <p className="text-gray-500 mb-0.5">Internal Notes</p>
                  <p className="text-gray-200">{request.notes}</p>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {request.status === "generated" &&
        (request.generatedOrders || []).length > 0 && (
          <div>
            <p className="text-xs text-gray-400 mb-2 font-medium">
              Generated {request.generatedOrders.length} order
              {request.generatedOrders.length !== 1 ? "s" : ""}:
            </p>
            <div className="space-y-1">
              {request.generatedOrders.map((mo) => (
                <div
                  key={mo._id || mo}
                  className="flex items-center justify-between bg-gray-800 rounded-lg px-3 py-2 text-xs"
                >
                  <span className="text-gray-200 font-mono">
                    {mo.orderNumber || "MO3-…"}
                  </span>
                  {mo.status && <StatusBadge status={mo.status} />}
                  {mo.vendor?.companyName && (
                    <span className="text-gray-400">
                      {mo.vendor.companyName}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

      <div className="flex gap-3 pt-1">
        {canGenerate && (
          <button
            onClick={handleGenerate}
            disabled={generating}
            className={cls(
              "px-5 py-2 text-sm font-semibold rounded-lg text-white transition-colors",
              generating
                ? "bg-gray-600 cursor-not-allowed"
                : "bg-orange-500 hover:bg-orange-600",
            )}
          >
            {generating
              ? "Generating…"
              : `Generate Orders (${uniqueVendors} vendor${uniqueVendors !== 1 ? "s" : ""})`}
          </button>
        )}
        {canCancel && (
          <button
            onClick={handleCancel}
            disabled={cancelling}
            className="px-4 py-2 text-sm text-red-400 hover:text-red-300 rounded-lg border border-red-800 hover:border-red-600"
          >
            {cancelling ? "Cancelling…" : "Cancel Request"}
          </button>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Attachments
// ─────────────────────────────────────────────────────────────────────────────
const ATTACHMENT_TYPE_META = {
  vendor_email: {
    label: "Sent to vendor",
    cls: "bg-purple-900/40 text-purple-300 border-purple-700/40",
  },
  site_email: {
    label: "Sent to site",
    cls: "bg-blue-900/40 text-blue-300 border-blue-700/40",
  },
  packing_slip: {
    label: "Packing slip",
    cls: "bg-green-900/40 text-green-300 border-green-700/40",
  },
  delivery: {
    label: "Delivery",
    cls: "bg-green-900/40 text-green-300 border-green-700/40",
  },
  order: {
    label: "Order",
    cls: "bg-orange-900/40 text-orange-300 border-orange-700/40",
  },
  other: {
    label: "Other",
    cls: "bg-gray-700/60 text-gray-300 border-gray-600",
  },
};

const attMeta = (type) =>
  ATTACHMENT_TYPE_META[type] || ATTACHMENT_TYPE_META.other;

const isImageAtt = (a) =>
  /\.(png|jpe?g|gif|webp|heic|bmp)$/i.test(a?.name || "") ||
  (a?.url || "").includes("/image/upload/");

const isPdfAtt = (a) => /\.pdf$/i.test(a?.name || "");

// Cloudinary thumbnail (images only)
const thumbUrl = (url) =>
  url.includes("/image/upload/")
    ? url.replace("/upload/", "/upload/w_320,h_320,c_fill,q_auto/")
    : url;

// Normalise attachments + legacy orderAttachments/deliveryAttachments into one list
const collectAttachments = (order) => {
  const norm = (list, fallbackType) =>
    (list || [])
      .map((a) =>
        typeof a === "string"
          ? { url: a, name: a.split("/").pop(), type: fallbackType }
          : { ...a, type: a.type || fallbackType },
      )
      .filter((a) => a.url);
  return [
    ...norm(order.attachments, "other"),
    ...norm(order.orderAttachments, "order"),
    ...norm(order.deliveryAttachments, "delivery"),
  ];
};

// PDFs from Cloudinary "raw" uploads have no extension, so fetch as a blob and
// open with the right MIME type so the browser renders instead of downloading.
const openAttachment = async (att) => {
  if (!isPdfAtt(att)) {
    window.open(att.url, "_blank", "noopener");
    return;
  }
  const w = window.open("", "_blank"); // open synchronously to dodge popup blockers
  try {
    const res = await fetch(att.url);
    const blob = await res.blob();
    const pdf = new Blob([blob], { type: "application/pdf" });
    const objUrl = URL.createObjectURL(pdf);
    if (w) w.location.href = objUrl;
    else window.open(objUrl, "_blank");
    setTimeout(() => URL.revokeObjectURL(objUrl), 60_000);
  } catch {
    if (w) w.location.href = att.url;
    else window.open(att.url, "_blank", "noopener");
  }
};

const downloadAttachment = async (att) => {
  try {
    const res = await fetch(att.url);
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = att.name || "file";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(a.href);
  } catch {
    window.open(att.url, "_blank", "noopener");
  }
};

function AttachmentsPanel({ order }) {
  const [lightbox, setLightbox] = useState(null);
  const all = useMemo(() => collectAttachments(order), [order]);

  if (!all.length) return null;

  const fmtDate = (d) =>
    d
      ? new Date(d).toLocaleString(undefined, {
          month: "short",
          day: "numeric",
          year: "numeric",
          hour: "numeric",
          minute: "2-digit",
        })
      : "";

  const images = all.filter(isImageAtt);
  const files = all.filter((a) => !isImageAtt(a));

  return (
    <div className="space-y-3">
      <p className="text-xs text-gray-400 font-medium">
        Attachments <span className="text-gray-600">({all.length})</span>
      </p>

      {/* Documents */}
      {files.length > 0 && (
        <div className="space-y-1.5">
          {files.map((att, i) => {
            const meta = attMeta(att.type);
            return (
              <div
                key={att._id || i}
                className="flex items-center gap-3 bg-gray-900 border border-gray-700 rounded-lg px-3 py-2"
              >
                <div className="shrink-0 w-9 h-9 rounded-md bg-gray-800 border border-gray-700 flex items-center justify-center text-[10px] font-bold text-orange-400">
                  {isPdfAtt(att) ? "PDF" : "FILE"}
                </div>
                <div className="flex-1 min-w-0">
                  <p
                    className="text-xs text-gray-200 truncate"
                    title={att.name}
                  >
                    {att.name || "Untitled"}
                  </p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span
                      className={cls(
                        "text-[9px] px-1.5 py-0.5 rounded font-medium border",
                        meta.cls,
                      )}
                    >
                      {meta.label}
                    </span>
                    {att.uploadedAt && (
                      <span className="text-[10px] text-gray-500">
                        {fmtDate(att.uploadedAt)}
                        {att.uploadedBy?.displayName
                          ? ` · ${att.uploadedBy.displayName}`
                          : ""}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button
                    type="button"
                    onClick={() => openAttachment(att)}
                    className="px-2.5 py-1 text-[10px] font-semibold rounded-lg bg-orange-500 hover:bg-orange-600 text-white"
                  >
                    Open
                  </button>
                  <button
                    type="button"
                    onClick={() => downloadAttachment(att)}
                    className="px-2.5 py-1 text-[10px] font-medium rounded-lg bg-gray-700 hover:bg-gray-600 text-gray-300 border border-gray-600"
                  >
                    ⬇
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Images */}
      {images.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {images.map((att, i) => {
            const meta = attMeta(att.type);
            return (
              <button
                key={att._id || i}
                type="button"
                onClick={() => setLightbox(att)}
                className="group relative text-left bg-gray-900 border border-gray-700 hover:border-orange-500 rounded-lg overflow-hidden transition-colors"
                title={att.name}
              >
                <img
                  src={thumbUrl(att.url)}
                  alt={att.name || "attachment"}
                  loading="lazy"
                  className="w-full h-32 object-cover"
                />
                <div className="px-2 py-1.5">
                  <span
                    className={cls(
                      "text-[9px] px-1.5 py-0.5 rounded font-medium border",
                      meta.cls,
                    )}
                  >
                    {meta.label}
                  </span>
                  {att.uploadedAt && (
                    <p className="text-[10px] text-gray-500 mt-1">
                      {fmtDate(att.uploadedAt)}
                    </p>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[99999] bg-black/85 flex flex-col items-center justify-center p-4"
          onClick={() => setLightbox(null)}
        >
          <div
            className="flex items-center gap-2 mb-3"
            onClick={(e) => e.stopPropagation()}
          >
            <span className="text-xs text-gray-300 max-w-[60vw] truncate">
              {lightbox.name}
            </span>
            <button
              type="button"
              onClick={() => downloadAttachment(lightbox)}
              className="px-2.5 py-1 text-[10px] font-medium rounded-lg bg-gray-700 hover:bg-gray-600 text-gray-200 border border-gray-600"
            >
              ⬇ Download
            </button>
            <button
              type="button"
              onClick={() => window.open(lightbox.url, "_blank", "noopener")}
              className="px-2.5 py-1 text-[10px] font-semibold rounded-lg bg-orange-500 hover:bg-orange-600 text-white"
            >
              Open original
            </button>
            <button
              type="button"
              onClick={() => setLightbox(null)}
              className="text-gray-300 hover:text-white text-2xl leading-none ml-2"
            >
              ×
            </button>
          </div>
          <img
            src={lightbox.url}
            alt={lightbox.name}
            onClick={(e) => e.stopPropagation()}
            className="max-w-full max-h-[80vh] object-contain rounded-lg"
          />
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MODetailModal — unchanged except removing the unused canConfirm variable
// ─────────────────────────────────────────────────────────────────────────────
function MODetailModal({ order, token, onUpdated, onClose }) {
  const [deliveryQtys, setDeliveryQtys] = useState(
    Object.fromEntries(
      (order.items || []).map((i) => [i._id, i.qtyPending || 0]),
    ),
  );
  const [varianceReasons, setVarianceReasons] = useState({});
  const [delivering, setDelivering] = useState(false);
  const [voiding, setVoiding] = useState(false);
  const [voidReason, setVoidReason] = useState("");
  const [showVoid, setShowVoid] = useState(false);
  const [error, setError] = useState("");

  const canDeliver = ["confirmed", "partial"].includes(order.status);
  const canVoid = ["confirmed", "partial"].includes(order.status);

  const h = { headers: { Authorization: `Bearer ${token}` } };
  const moTotal = (order.items || []).reduce((s, i) => s + (i.total || 0), 0);

  const handleDeliver = async () => {
    setDelivering(true);
    setError("");
    try {
      const items = (order.items || [])
        .map((i) => ({
          itemId: i._id,
          qtyDelivered: parseFloat(deliveryQtys[i._id]) || 0,
          varianceReason: varianceReasons[i._id] || undefined,
        }))
        .filter((i) => i.qtyDelivered > 0);
      if (!items.length) {
        setError("Enter at least one delivered quantity.");
        setDelivering(false);
        return;
      }
      const res = await axios.post(
        `${API}/materialOrders3/${order._id}/deliver`,
        { items },
        h,
      );
      onUpdated(res.data.order);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setDelivering(false);
    }
  };

  const handleVoid = async () => {
    setVoiding(true);
    setError("");
    try {
      const res = await axios.post(
        `${API}/materialOrders3/${order._id}/void`,
        { voidReason },
        h,
      );
      onUpdated(res.data.order);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setVoiding(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[99998] flex items-center justify-center bg-black/60">
      <div className="bg-gray-800 border border-gray-600 rounded-xl shadow-2xl w-full max-w-3xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-700 sticky top-0 bg-gray-800 z-10">
          <div>
            <h3 className="text-base font-semibold text-white">
              MO# {order.orderNumber}
            </h3>
            <p className="text-xs text-gray-400 mt-0.5">
              {order.vendor?.companyName || "No vendor"} · PO #
              {order.purchaseOrder?.poNumber}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <StatusBadge status={order.status} />
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white text-xl leading-none"
            >
              ×
            </button>
          </div>
        </div>
        <div className="p-5 space-y-4">
          {error && (
            <div className="px-3 py-2 bg-red-900/50 border border-red-700 rounded-lg text-xs text-red-300">
              {error}
            </div>
          )}
          <div className="overflow-x-auto rounded-lg border border-gray-700">
            <table className="w-full text-xs">
              <thead className="bg-gray-900">
                <tr>
                  <th className="px-3 py-2 text-left text-gray-400">
                    Description
                  </th>
                  <th className="px-3 py-2 text-left text-gray-400">Size</th>
                  <th className="px-3 py-2 text-center text-gray-400">
                    Coverage
                  </th>
                  <th className="px-3 py-2 text-right text-gray-400">
                    Ordered
                  </th>
                  <th className="px-3 py-2 text-right text-gray-400">
                    Delivered
                  </th>
                  <th className="px-3 py-2 text-right text-gray-400">
                    Pending
                  </th>
                  {canDeliver && (
                    <th className="px-3 py-2 text-right text-gray-400">
                      Deliver Now
                    </th>
                  )}
                  <th className="px-3 py-2 text-right text-gray-400">$/PC</th>
                  <th className="px-3 py-2 text-right text-gray-400">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700/50">
                {(order.items || []).map((item, i) => {
                  const covered = isCoverageBased(item.uom);
                  return (
                    <tr
                      key={item._id || i}
                      className={
                        i % 2 === 0 ? "bg-gray-800/40" : "bg-gray-700/20"
                      }
                    >
                      <td className="px-3 py-2">
                        <p className="text-gray-200">{item.description}</p>
                        <p className="text-[10px] text-orange-400 font-mono">
                          {item.itemCode}
                        </p>
                        {item.isNewPoItem && (
                          <span className="text-[9px] bg-yellow-600/60 text-yellow-100 px-1 py-0.5 rounded">
                            NEW
                          </span>
                        )}
                        {item.notes?.trim() && (
                          <p className="text-[10px] text-amber-300/80 italic mt-0.5 whitespace-normal">
                            📝 {item.notes}
                          </p>
                        )}
                      </td>
                      <td className="px-3 py-2 text-gray-400">
                        {item.size || "—"}
                      </td>
                      <td className="px-3 py-2 text-center">
                        {covered ? (
                          <span className="text-[10px] text-blue-300 font-mono">
                            {item.coveragePerUnit} {item.uom}/PC
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="px-3 py-2 text-right text-gray-300 font-mono">
                        {item.quantity}
                      </td>
                      <td className="px-3 py-2 text-right text-green-300 font-mono">
                        {item.qtyDelivered || 0}
                      </td>
                      <td className="px-3 py-2 text-right text-yellow-300 font-mono">
                        {item.qtyPending || 0}
                      </td>
                      {canDeliver && (
                        <td className="px-3 py-2">
                          <div className="flex flex-col items-end gap-1">
                            <input
                              type="number"
                              value={
                                deliveryQtys[item._id] ?? item.qtyPending ?? 0
                              }
                              min="0"
                              max={item.qtyPending || 0}
                              onWheel={(e) => e.target.blur()}
                              onChange={(e) =>
                                setDeliveryQtys((d) => ({
                                  ...d,
                                  [item._id]: e.target.value,
                                }))
                              }
                              className="w-20 px-2 py-1 text-xs bg-gray-700 border border-gray-600 rounded text-gray-200 text-right focus:border-orange-500 focus:outline-none"
                            />
                            {parseFloat(deliveryQtys[item._id] || 0) <
                              (item.qtyPending || 0) && (
                              <input
                                type="text"
                                value={varianceReasons[item._id] || ""}
                                placeholder="Variance reason…"
                                onChange={(e) =>
                                  setVarianceReasons((v) => ({
                                    ...v,
                                    [item._id]: e.target.value,
                                  }))
                                }
                                className="w-36 px-2 py-1 text-[10px] bg-gray-700 border border-yellow-700/50 rounded text-gray-400 focus:border-orange-500 focus:outline-none"
                              />
                            )}
                          </div>
                        </td>
                      )}
                      <td className="px-3 py-2 text-right text-gray-300 font-mono">
                        ${fmt(item.unitPrice || 0)}
                      </td>
                      <td className="px-3 py-2 text-right text-green-300 font-mono font-semibold">
                        ${fmt(item.total || 0)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="bg-gray-900 border-t border-gray-700">
                <tr>
                  <td
                    colSpan={canDeliver ? 7 : 6}
                    className="px-3 py-2 text-xs text-gray-400"
                  >
                    Order total
                  </td>
                  <td />
                  <td className="px-3 py-2 text-right text-base font-bold text-green-300 font-mono">
                    ${fmt(moTotal)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
            {[
              { l: "Shipping", v: order.shippingMethod },
              {
                l: "Expected",
                v: order.expectedDeliveryDate
                  ? new Date(order.expectedDeliveryDate).toLocaleDateString()
                  : "—",
              },
              { l: "Contact", v: order.deliveryContact?.displayName || "—" },
            ].map(({ l, v }) => (
              <div key={l} className="bg-gray-900 rounded-lg p-3">
                <p className="text-gray-500 mb-0.5">{l}</p>
                <p className="text-gray-200">{v}</p>
              </div>
            ))}
          </div>
          {order.notes?.trim() && (
            <div className="bg-gray-900 rounded-lg p-3 text-xs">
              <p className="text-gray-500 mb-0.5">Internal Notes</p>
              <p className="text-gray-200 whitespace-pre-wrap">{order.notes}</p>
            </div>
          )}
          <div className="flex flex-wrap gap-3 pt-1">
            {canDeliver && (
              <button
                onClick={handleDeliver}
                disabled={delivering}
                className={cls(
                  "px-4 py-2 text-sm font-semibold rounded-lg text-white transition-colors",
                  delivering
                    ? "bg-gray-600 cursor-not-allowed"
                    : "bg-green-600 hover:bg-green-700",
                )}
              >
                {delivering ? "Recording…" : "Record Delivery"}
              </button>
            )}
            {canVoid && !showVoid && (
              <button
                onClick={() => setShowVoid(true)}
                className="px-4 py-2 text-sm text-red-400 hover:text-red-300 rounded-lg border border-red-800 hover:border-red-600"
              >
                Void Order
              </button>
            )}
          </div>
          {showVoid && (
            <div className="bg-red-950/30 border border-red-800/50 rounded-lg p-4 space-y-3">
              <p className="text-sm text-red-300 font-medium">
                Void this order?
              </p>
              <textarea
                value={voidReason}
                onChange={(e) => setVoidReason(e.target.value)}
                placeholder="Reason for voiding…"
                rows={2}
                className="w-full px-3 py-2 text-sm bg-gray-800 border border-gray-600 rounded-lg text-gray-200 focus:border-red-500 focus:outline-none resize-none"
              />
              <div className="flex gap-3">
                <button
                  onClick={handleVoid}
                  disabled={voiding}
                  className="px-4 py-2 text-sm font-semibold bg-red-700 hover:bg-red-600 text-white rounded-lg"
                >
                  {voiding ? "Voiding…" : "Confirm Void"}
                </button>
                <button
                  onClick={() => setShowVoid(false)}
                  className="text-sm text-gray-400 hover:text-white"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main MaterialsTab3
// ─────────────────────────────────────────────────────────────────────────────
export default function MaterialsTab3({ project, setProject }) {
  const projectId = project?._id;
  const token = localStorage.getItem("token");
  const headers = useMemo(
    () => ({ Authorization: `Bearer ${token}` }),
    [token],
  );

  const [requests, setRequests] = useState([]);
  const [orders, setOrders] = useState([]);
  const [purchaseOrders, setPOs] = useState([]);
  const [users, setUsers] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [priceMap, setPriceMap] = useState({}); // { [itemCode]: { [vendorId]: { unitPrice, ... } } }
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const STORAGE_KEY = `materials_${projectId}`;

  const [activeView, setActiveViewState] = useState(() => {
    try {
      return localStorage.getItem(`${STORAGE_KEY}_view`) || "orders";
    } catch {
      return "orders";
    }
  });

  const [statusFilter, setStatusFilterState] = useState(() => {
    try {
      return localStorage.getItem(`${STORAGE_KEY}_status`) || "all";
    } catch {
      return "all";
    }
  });

  const setActiveView = (view) => {
    setActiveViewState(view);
    try {
      localStorage.setItem(`${STORAGE_KEY}_view`, view);
    } catch {}
  };

  const setStatusFilter = (status) => {
    setStatusFilterState(status);
    try {
      localStorage.setItem(`${STORAGE_KEY}_status`, status);
    } catch {}
  };
  const [showNew, setShowNew] = useState(false);
  // Replace useState for orderSort:
  const [orderSort, setOrderSort] = useState(() => ({
    col: searchParams.get("sort") || "orderNumber",
    dir: searchParams.get("dir") || "asc",
  }));

  const [selectedReq, setSelectedReq] = useState(null);
  const [selectedMO, setSelectedMO] = useState(null);
  const [showBulkSiteUpdate, setShowBulkSiteUpdate] = useState(false);
  const [billMap, setBillMap] = useState({});
  const [selectedOrderIds, setSelectedOrderIds] = useState(new Set());
  const [companyProfile, setCompanyProfile] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [userEmail, setUserEmail] = useState("");
  const [showBulkDeliver, setShowBulkDeliver] = useState(false);
  const [showBulkEmail, setShowBulkEmail] = useState(false);
  // ── CHANGE 2: Replace loadAll with this version that also fetches bills ───────
  const loadAll = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const [
        reqR,
        moR,
        poR,
        uR,
        vendorR,
        priceMapR,
        billsR,
        companyR,
        contactsR,
      ] = await Promise.all([
        axios
          .get(`${API}/materialRequests3/project/${projectId}`, { headers })
          .catch(() => ({ data: { requests: [] } })),
        axios
          .get(`${API}/materialOrders3/project/${projectId}`, { headers })
          .catch(() => ({ data: { orders: [] } })),
        axios
          .get(`${API}/purchaseOrders3/project/${projectId}`, { headers })
          .catch(() => ({ data: { purchaseOrders: [] } })),
        axios.get(`${API}/users`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/accounts`, { headers }).catch(() => ({ data: [] })),
        axios
          .get(`${API}/materialOrders3/project/${projectId}/vendor-price-map`, {
            headers,
          })
          .catch(() => ({ data: { priceMap: {} } })),
        axios
          .get(`${API}/bills3/project/${projectId}`, { headers })
          .catch(() => ({ data: { bills: [] } })),
        axios
          .get(`${API}/company-profile`, { headers })
          .catch(() => ({ data: null })),
        axios.get(`${API}/contacts`, { headers }).catch(() => ({ data: [] })),
      ]);

      setRequests(reqR.data.requests || []);
      setOrders(moR.data.orders || []);
      setPOs(poR.data.purchaseOrders || []);
      setUsers(
        (uR.data || []).filter((u) =>
          ["admin", "project_manager", "foreman"].includes(u.role),
        ),
      );
      setVendors((vendorR.data || []).filter((v) => v.type === "vendor"));
      setPriceMap(priceMapR.data?.priceMap || {});
      setCompanyProfile(companyR.data);
      setContacts(contactsR.data || []);

      const map = {};
      for (const bill of billsR.data.bills || []) {
        const moId =
          bill.relatedMO3?._id?.toString() ||
          (typeof bill.relatedMO3 === "string" ? bill.relatedMO3 : null);
        if (moId) map[moId] = bill.processingStatus;
      }
      setBillMap(map);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [projectId, headers]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        setUserEmail(jwtDecode(token).email || "");
      } catch {}
    }
  }, []);

  const filteredOrders = useMemo(() => {
    let list =
      statusFilter === "all"
        ? orders
        : orders.filter((o) => o.status === statusFilter);

    if (orderSort.col) {
      list = [...list].sort((a, b) => {
        const dir = orderSort.dir === "asc" ? 1 : -1;
        if (orderSort.col === "date") {
          const aD = a.expectedDeliveryDate
            ? new Date(a.expectedDeliveryDate)
            : new Date(0);
          const bD = b.expectedDeliveryDate
            ? new Date(b.expectedDeliveryDate)
            : new Date(0);
          return (aD - bD) * dir;
        }
        if (orderSort.col === "vendor") {
          const aV = a.vendor?.companyName?.toLowerCase() || "";
          const bV = b.vendor?.companyName?.toLowerCase() || "";
          return aV.localeCompare(bV) * dir;
        }
        if (orderSort.col === "orderNumber") {
          return ((b.orderNumber || 0) - (a.orderNumber || 0)) * dir;
        }
        return 0;
        if (orderSort.col === "status") {
          const order = [
            "pending",
            "confirmed",
            "partial",
            "delivered",
            "void",
          ];
          return (order.indexOf(a.status) - order.indexOf(b.status)) * dir;
        }
        if (orderSort.col === "bill") {
          const rank = (id) => {
            const s = billMap[id?.toString()];
            if (!s) return 2;
            if (s === "processed") return 0;
            return 1;
          };
          return (rank(a._id) - rank(b._id)) * dir;
        }
        return (b.orderNumber || 0) - (a.orderNumber || 0);
      });
    } else {
      list = [...list].sort(
        (a, b) => (b.orderNumber || 0) - (a.orderNumber || 0),
      );
    }

    return list;
  }, [orders, statusFilter, orderSort, billMap]);

  const requestById = useMemo(
    () => new Map(requests.map((r) => [String(r._id), r])),
    [requests],
  );

  // moId -> request that generated it (from the request's generatedOrders)
  const requestByMoId = useMemo(() => {
    const map = new Map();
    requests.forEach((r) =>
      (r.generatedOrders || []).forEach((mo) =>
        map.set(String(mo._id || mo), r),
      ),
    );
    return map;
  }, [requests]);

  // Prefer the request's generatedOrders; fall back to the MO's materialRequest ref
  const getRequestForOrder = (o) =>
    requestByMoId.get(String(o._id)) ||
    requestById.get(
      String(o.materialRequest?._id || o.materialRequest || ""),
    ) ||
    null;

  // Replace toggleSort:
  const toggleSort = (col) => {
    const next = {
      col,
      dir: orderSort.col === col && orderSort.dir === "asc" ? "desc" : "asc",
    };
    setOrderSort(next);
    setSearchParams(
      (p) => {
        p.set("sort", next.col);
        p.set("dir", next.dir);
        return p;
      },
      { replace: true },
    );
  };

  const exportOrdersToExcel = useCallback(async () => {
    const XLSX = await import("xlsx");

    const rows = filteredOrders.flatMap((o) =>
      (o.items || []).map((item) => ({
        "Order #": o.orderNumber,
        Vendor: o.vendor?.companyName || "—",
        "PO #": o.purchaseOrder?.poNumber || "—",
        Description: o.description || "", // ADD
        "PO Title": o.purchaseOrder?.title || "—",
        Status: o.status,
        "Item Code": item.itemCode || "",
        Description: item.description || "",

        Size: item.size || "",
        UOM: item.uom || "",
        "Coverage/PC": item.coveragePerUnit || 1,
        "Qty (PC)": item.quantity || 0,
        "Unit Price": item.unitPrice || 0,
        "Line Total": (item.quantity || 0) * (item.unitPrice || 0),
        "Qty Delivered": item.qtyDelivered || 0,
        "Qty Pending": item.qtyPending || 0,
        "Expected Delivery": o.expectedDeliveryDate
          ? new Date(o.expectedDeliveryDate).toLocaleDateString()
          : "",
        "Bill Status": billMap[o._id?.toString()] || "No bill",
        "Sent to Vendor":
          o.sentToVendor === true || ["partial", "delivered"].includes(o.status)
            ? "Yes"
            : "No",
      })),
    );

    if (!rows.length) return;

    const ws = XLSX.utils.json_to_sheet(rows);

    // Reasonable column widths
    ws["!cols"] = [
      { wch: 10 },
      { wch: 22 },
      { wch: 8 },
      { wch: 24 },
      { wch: 12 },
      { wch: 14 },
      { wch: 30 },
      { wch: 12 },
      { wch: 8 },
      { wch: 12 },
      { wch: 10 },
      { wch: 12 },
      { wch: 12 },
      { wch: 12 },
      { wch: 12 },
      { wch: 14 },
      { wch: 12 },
      { wch: 14 },
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Material Orders");

    const filterLabel = statusFilter === "all" ? "all" : statusFilter;
    const filename = `materials_${project?.projectName || projectId}_${filterLabel}_${new Date().toISOString().split("T")[0]}.xlsx`;

    XLSX.writeFile(wb, filename);
  }, [filteredOrders, billMap, statusFilter, project, projectId]);

  const SortIcon = ({ col }) => {
    if (orderSort.col !== col)
      return <span className="ml-1 text-gray-600">⇅</span>;
    return (
      <span className="ml-1 text-orange-400">
        {orderSort.dir === "asc" ? "↑" : "↓"}
      </span>
    );
  };

  const selectedOrders = useMemo(
    () => orders.filter((o) => selectedOrderIds.has(o._id?.toString())),
    [orders, selectedOrderIds],
  );

  const filteredRequests = useMemo(
    () =>
      statusFilter === "all"
        ? requests
        : requests.filter((r) => r.status === statusFilter),
    [requests, statusFilter],
  );
  const summary = useMemo(
    () => ({
      pending: orders.filter((o) => o.status === "pending").length,
      confirmed: orders.filter((o) => o.status === "confirmed").length,
      partial: orders.filter((o) => o.status === "partial").length,
      delivered: orders.filter((o) => o.status === "delivered").length,
      void: orders.filter((o) => o.status === "void").length,
    }),
    [orders],
  );

  if (loading)
    return (
      <div className="flex items-center justify-center py-16 gap-3">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
        <span className="text-gray-400 text-sm">Loading materials…</span>
      </div>
    );

  return (
    <div className="space-y-4">
      {selectedMO && (
        <MODetailModal
          order={selectedMO}
          token={token}
          onUpdated={(o) => {
            setOrders((p) => p.map((x) => (x._id === o._id ? o : x)));
            setSelectedMO(o);
          }}
          onClose={() => setSelectedMO(null)}
        />
      )}

      {/* Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        {[
          ["Pending", summary.pending, "text-yellow-300"],
          ["Confirmed", summary.confirmed, "text-blue-300"],
          ["Partial", summary.partial, "text-orange-300"],
          ["Delivered", summary.delivered, "text-green-300"],
          ["Void", summary.void, "text-red-400"],
        ].map(([label, count, color]) => (
          <div
            key={label}
            className="bg-gray-800 rounded-xl border border-gray-700 px-4 py-3 text-center"
          >
            <p className={cls("text-2xl font-bold", color)}>{count}</p>
            <p className="text-xs text-gray-400 mt-0.5">{label}</p>
          </div>
        ))}
      </div>

      {/* Controls */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex rounded-lg overflow-hidden border border-gray-600">
          {[
            ["orders", `Orders (${orders.length})`],
            ["requests", `Requests (${requests.length})`],
            ["summary", "Summary"],
          ].map(([id, label]) => (
            <button
              key={id}
              onClick={() => {
                setActiveView(id);
                setStatusFilter("all");
                setSelectedReq(null);
              }}
              className={cls(
                "px-4 py-2 text-sm font-medium transition-colors",
                activeView === id
                  ? "bg-orange-500 text-white"
                  : "bg-gray-700 text-gray-400 hover:bg-gray-600 hover:text-white",
              )}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 text-sm bg-gray-700 border border-gray-600 rounded-lg text-gray-300 focus:border-orange-500 focus:outline-none"
          >
            <option value="all">All Statuses</option>
            {(activeView === "orders"
              ? ["pending", "confirmed", "partial", "delivered", "void"]
              : ["draft", "submitted", "generated", "cancelled"]
            ).map((s) => (
              <option key={s} value={s}>
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </option>
            ))}
          </select>
          <button
            onClick={() => {
              setShowNew(true);
              setSelectedReq(null);
              setActiveView("requests");
            }}
            className="px-4 py-2 text-sm font-semibold bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
          >
            + New Request
          </button>
          <button
            onClick={exportOrdersToExcel}
            disabled={!filteredOrders.length}
            title="Export current orders view to Excel"
            className={cls(
              "px-4 py-2 text-sm font-semibold rounded-lg transition-colors",
              filteredOrders.length
                ? "bg-gray-700 hover:bg-gray-600 text-gray-200 border border-gray-600"
                : "bg-gray-800 text-gray-600 border border-gray-700 cursor-not-allowed",
            )}
          >
            ⬇ Export to Excel
          </button>
        </div>
      </div>

      {showNew && (
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-white">
              New Material Request
            </h3>
            <button
              onClick={() => setShowNew(false)}
              className="text-gray-400 hover:text-white text-xl leading-none"
            >
              ×
            </button>
          </div>
          <RequestForm
            projectId={projectId}
            purchaseOrders={purchaseOrders}
            users={users}
            token={token}
            priceMap={priceMap}
            vendors={vendors}
            onSaved={(r) => {
              setRequests((p) => [r, ...p]);
              setShowNew(false);
              setSelectedReq(r);
              setActiveView("requests");
            }}
            onCancel={() => setShowNew(false)}
          />
        </div>
      )}

      {selectedReq && !showNew && activeView === "requests" && (
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-5">
          <RequestDetail
            request={selectedReq}
            token={token}
            priceMap={priceMap}
            vendors={vendors}
            purchaseOrders={purchaseOrders}
            users={users}
            projectId={projectId}
            onGenerated={(r) => {
              setRequests((p) => p.map((x) => (x._id === r._id ? r : x)));
              setSelectedReq(r);
              loadAll();
            }}
            onCancelled={(id) => {
              setRequests((p) =>
                p.map((r) =>
                  r._id === id ? { ...r, status: "cancelled" } : r,
                ),
              );
              setSelectedReq(null);
            }}
            onUpdated={(r) => {
              setRequests((p) => p.map((x) => (x._id === r._id ? r : x)));
              setSelectedReq(r);
            }}
            onDuplicated={(r) => {
              setRequests((p) => [r, ...p]);
              setSelectedReq(r);
              setActiveView("requests");
            }}
            onClose={() => setSelectedReq(null)}
          />
        </div>
      )}

      {activeView === "orders" && (
        <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-900">
              <tr>
                <th className="px-4 py-3 w-8">
                  <SelectAllCheckbox
                    orders={filteredOrders}
                    selectedOrderIds={selectedOrderIds}
                    setSelectedOrderIds={setSelectedOrderIds}
                    eligibleStatuses={["pending", "confirmed", "partial"]}
                  />
                </th>
                <th
                  className="px-4 py-3 text-left text-xs text-gray-400 cursor-pointer hover:text-white select-none"
                  onClick={() => toggleSort("orderNumber")}
                >
                  Order # <SortIcon col="orderNumber" />
                </th>
                <th className="px-4 py-3 text-left text-xs text-gray-400">
                  Request
                </th>
                <th
                  className="px-4 py-3 text-left text-xs text-gray-400 cursor-pointer hover:text-white select-none"
                  onClick={() => toggleSort("vendor")}
                >
                  Vendor <SortIcon col="vendor" />
                </th>
                <th className="px-4 py-3 text-left text-xs text-gray-400">
                  PO
                </th>
                <th className="px-4 py-3 text-left text-xs text-gray-400">
                  Description
                </th>
                <th className="px-4 py-3 text-right text-xs text-gray-400">
                  Total
                </th>
                <th
                  className="px-4 py-3 text-left text-xs text-gray-400 cursor-pointer hover:text-white select-none"
                  onClick={() => toggleSort("date")}
                >
                  Expected <SortIcon col="date" />
                </th>
                <th
                  className="px-4 py-3 text-center text-xs text-gray-400 cursor-pointer hover:text-white select-none"
                  onClick={() => toggleSort("status")}
                >
                  Status <SortIcon col="status" />
                </th>
                <th
                  className="px-4 py-3 text-center text-xs text-gray-400 cursor-pointer hover:text-white select-none"
                  onClick={() => toggleSort("bill")}
                >
                  Bill <SortIcon col="bill" />
                </th>
                <th className="px-4 py-3 text-center text-xs text-gray-400">
                  Email Status
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700/50">
              {filteredOrders.map((o, i) => {
                const sourceReq = getRequestForOrder(o);
                return (
                  <tr
                    key={o._id}
                    onClick={() =>
                      navigate(
                        `/projects/${projectId}/material-orders3/${o._id}`,
                      )
                    }
                    className={cls(
                      "cursor-pointer hover:bg-gray-700/60 transition-colors",
                      i % 2 === 0 ? "bg-gray-800/40" : "bg-gray-700/20",
                    )}
                  >
                    <td
                      className="px-4 py-3"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <RowCheckbox
                        order={o}
                        selectedOrderIds={selectedOrderIds}
                        setSelectedOrderIds={setSelectedOrderIds}
                      />
                    </td>
                    <td className="px-4 py-3 font-mono text-orange-400 font-medium">
                      MO #{o.orderNumber}
                    </td>
                    <td
                      className="px-4 py-3"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {sourceReq ? (
                        <button
                          type="button"
                          onClick={() => {
                            setShowNew(false);
                            setActiveView("requests");
                            setStatusFilter("all");
                            setSelectedReq(sourceReq);
                          }}
                          title="Open the request that created this order"
                          className="font-mono text-xs text-blue-300 hover:text-blue-200 underline decoration-dotted"
                        >
                          MRQ #{sourceReq.requestNumber}
                        </button>
                      ) : (
                        <span className="text-gray-600 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-200">
                      {o.vendor?.companyName || "—"}
                    </td>
                    <td className="px-4 py-3 text-gray-400 text-xs">
                      PO #{o.purchaseOrder?.poNumber} - {o.purchaseOrder?.title}
                    </td>
                    <td
                      className="px-4 py-3 text-gray-300 text-xs max-w-[220px] truncate"
                      title={o.description || ""}
                    >
                      {o.description || (
                        <span className="text-gray-600">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-green-300 font-semibold">
                      $
                      {fmt(
                        o.total ||
                          (o.items || []).reduce(
                            (s, i) => s + (i.total || 0),
                            0,
                          ),
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-400 text-xs">
                      {o.expectedDeliveryDate
                        ? new Date(o.expectedDeliveryDate).toLocaleDateString()
                        : "—"}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <StatusBadge status={o.status} />
                    </td>
                    <td
                      className="px-4 py-3 text-center"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <BillStatusBadge status={billMap[o._id?.toString()]} />
                    </td>
                    <td className="px-4 py-3 text-center">
                      <SentBadge order={o} />
                    </td>
                  </tr>
                );
              })}
              {!filteredOrders.length && (
                <tr>
                  <td
                    colSpan={11}
                    className="px-4 py-10 text-center text-gray-500 text-sm"
                  >
                    {statusFilter === "all"
                      ? "No orders yet. Create a request to get started."
                      : `No ${statusFilter} orders.`}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      {activeView === "requests" && !showNew && !selectedReq && (
        <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-900">
              <tr>
                <th className="px-4 py-3 text-left text-xs text-gray-400">
                  Request #
                </th>
                <th className="px-4 py-3 text-left text-xs text-gray-400">
                  PO
                </th>
                <th className="px-4 py-3 text-left text-xs text-gray-400">
                  Notes
                </th>
                <th className="px-4 py-3 text-center text-xs text-gray-400">
                  Items
                </th>
                <th className="px-4 py-3 text-center text-xs text-gray-400">
                  Orders Created
                </th>
                <th className="px-4 py-3 text-left text-xs text-gray-400">
                  Created
                </th>
                <th className="px-4 py-3 text-center text-xs text-gray-400">
                  Status
                </th>
                <th className="px-4 py-3 text-center text-xs text-gray-400"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700/50">
              {filteredRequests.map((r, i) => (
                <tr
                  key={r._id}
                  onClick={() => setSelectedReq(r)}
                  className={cls(
                    "cursor-pointer hover:bg-gray-700/60 transition-colors",
                    i % 2 === 0 ? "bg-gray-800/40" : "bg-gray-700/20",
                  )}
                >
                  <td className="px-4 py-3 font-mono text-orange-400 font-medium">
                    MRQ #{r.requestNumber}
                  </td>
                  <td className="px-4 py-3 text-gray-400 text-xs">
                    PO #{r.purchaseOrder?.poNumber} — {r.purchaseOrder?.title}
                  </td>
                  <td
                    className="px-4 py-3 text-gray-300 text-xs max-w-[220px] truncate"
                    title={getRequestNotesText(r)}
                  >
                    {getRequestNotesText(r) || (
                      <span className="text-gray-600">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center text-gray-300">
                    {(r.items || []).length}
                  </td>
                  <td
                    className="px-4 py-3 text-center"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {r.status === "generated" &&
                    (r.generatedOrders || []).length > 0 ? (
                      <div className="flex flex-wrap justify-center gap-1">
                        {r.generatedOrders.map((mo) => (
                          <button
                            key={mo._id || mo}
                            type="button"
                            disabled={!mo._id}
                            onClick={() =>
                              navigate(
                                `/projects/${projectId}/material-orders3/${mo._id}`,
                              )
                            }
                            title={mo.vendor?.companyName || "Open order"}
                            className="px-1.5 py-0.5 text-[10px] font-mono rounded bg-orange-900/30 text-orange-300 border border-orange-700/40 hover:bg-orange-800/40"
                          >
                            MO #{mo.orderNumber ?? "—"}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <span className="text-gray-600 text-xs">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-400 text-xs">
                    {new Date(r.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <StatusBadge status={r.status} />
                  </td>
                  <td
                    className="px-3 py-3 text-center"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <DuplicateButton
                      requestId={r._id}
                      token={token}
                      onDuplicated={(newReq) => {
                        setRequests((p) => [newReq, ...p]);
                        setSelectedReq(newReq);
                        setActiveView("requests");
                      }}
                    />
                  </td>
                </tr>
              ))}
              {!filteredRequests.length && (
                <tr>
                  <td
                    colSpan={8}
                    className="px-4 py-10 text-center text-gray-500 text-sm"
                  >
                    No material requests yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {activeView === "summary" && <MaterialsSummary3 orders={orders} />}
      {showBulkDeliver && selectedOrders.length > 0 && (
        <BulkDeliverModal
          orders={selectedOrders}
          token={token}
          onDelivered={(updatedOrders) => {
            setOrders((prev) =>
              prev.map((o) => {
                const updated = updatedOrders.find((u) => u._id === o._id);
                return updated || o;
              }),
            );
            setShowBulkDeliver(false);
            setSelectedOrderIds(new Set());
          }}
          onClose={() => setShowBulkDeliver(false)}
        />
      )}

      {showBulkSiteUpdate && selectedOrders.length > 0 && (
        <BulkSiteUpdateModal
          orders={selectedOrders}
          project={project}
          companyProfile={companyProfile}
          vendors={vendors}
          contacts={contacts}
          users={users}
          token={token}
          userEmail={userEmail}
          onSent={() => {
            setShowBulkSiteUpdate(false);
            setSelectedOrderIds(new Set());
          }}
          onClose={() => setShowBulkSiteUpdate(false)}
        />
      )}

      {showBulkEmail && selectedOrders.length > 0 && (
        <BulkEmailModal
          orders={selectedOrders}
          project={project}
          companyProfile={companyProfile}
          vendors={vendors}
          contacts={contacts}
          users={users}
          token={token}
          userEmail={userEmail}
          onSent={() => {
            setShowBulkEmail(false);
            setSelectedOrderIds(new Set());
          }}
          onClose={() => setShowBulkEmail(false)}
        />
      )}

      <BulkActionsBar
        selectedOrderIds={selectedOrderIds}
        orders={orders}
        onClearSelection={() => setSelectedOrderIds(new Set())}
        onDeliver={() => setShowBulkDeliver(true)}
        onEmail={() => setShowBulkEmail(true)}
        onSiteUpdate={() => setShowBulkSiteUpdate(true)}
      />
    </div>
  );
}
